import '@total-typescript/ts-reset';
import { z } from 'zod';
import { FetcherClient } from '@bylg/fetcher';

const envSchema = z.enum(["staging", "production", "testing"]);

const urlConfig$j = {
  staging: {
    driverTrackingUrl: "https://api-staging-internal.mozio.com/v2/data-entry/vehicle-tracking/driver-tracking-report",
    getVehiclesUrl: "https://api-staging-internal.mozio.com/v2/data-entry/vehicle-tracking/vehicles",
    driverTrackedRidesUrl: "https://api-staging-internal.mozio.com/v2/data-entry/vehicle-tracking/driver-tracking-report/driver-tracked-reservations",
    driverUntrackedRidesUrl: "https://api-staging-internal.mozio.com/v2/data-entry/vehicle-tracking/driver-tracking-report/driver-untracked-reservations",
    byDriverUrl: "https://api-staging-internal.mozio.com/v2/data-entry/providers/by-driver-user/",
    updateUserProviderUrl: "https://api-staging-internal.mozio.com/v2/auth/drivers/update-user-provider/",
    providersUrl: "https://api-staging-internal.mozio.com/v2/data-entry/providers/"
  },
  production: {
    driverTrackingUrl: "https://api.mozio.com/v2/data-entry/vehicle-tracking/driver-tracking-report",
    getVehiclesUrl: "https://api.mozio.com/v2/data-entry/vehicle-tracking/vehicles",
    driverTrackedRidesUrl: "https://api.mozio.com/v2/data-entry/vehicle-tracking/driver-tracking-report/driver-tracked-reservations",
    driverUntrackedRidesUrl: "https://api.mozio.com/v2/data-entry/vehicle-tracking/driver-tracking-report/driver-untracked-reservations",
    byDriverUrl: "https://api.mozio.com/v2/data-entry/providers/by-driver-user/",
    updateUserProviderUrl: "https://api.mozio.com/v2/auth/drivers/update-user-provider/",
    providersUrl: "https://api.mozio.com/v2/data-entry/providers/"
  },
  testing: {
    driverTrackingUrl: "https://api-testing.mozio.com/v2/data-entry/vehicle-tracking/driver-tracking-report",
    getVehiclesUrl: "https://api-testing.mozio.com/v2/data-entry/vehicle-tracking/vehicles",
    driverTrackedRidesUrl: "https://api-testing.mozio.com/v2/data-entry/vehicle-tracking/driver-tracking-report/driver-tracked-reservations",
    driverUntrackedRidesUrl: "https://api-testing.mozio.com/v2/data-entry/vehicle-tracking/driver-tracking-report/driver-untracked-reservations",
    byDriverUrl: "https://api-testing.mozio.com/v2/data-entry/providers/by-driver-user/",
    updateUserProviderUrl: "https://api-testing.mozio.com/v2/auth/drivers/update-user-provider/",
    providersUrl: "https://api-testing.mozio.com/v2/data-entry/providers/"
  }
};
const f$i = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    authorization: z.string()
  }
});
const getDriversRides = f$i.fetcher(
  async ({ ctx, get }, { page, startDate, endDate }) => {
    const { driverTrackingUrl } = urlConfig$j[ctx.env];
    const pageSize = 6;
    const params = {
      page_size: pageSize.toString(),
      page: page.toString(),
      start_date: startDate.toISOString().split("T")[0],
      end_date: endDate.toISOString().split("T")[0]
    };
    const paramsString = new URLSearchParams(params).toString();
    const { data } = await get(
      `${driverTrackingUrl}/table/?${paramsString}`,
      z.object({
        count: z.number(),
        next: z.string().url().nullable(),
        previous: z.string().url().nullable(),
        results: z.array(
          z.object({
            email: z.string().min(1).nullable(),
            name: z.string().min(1),
            total_ride_count: z.number(),
            tracked_ride_count: z.number()
          })
        )
      })
    );
    return {
      drivers: data.results.map(
        (driver) => ({
          id: driver.email ?? driver.name,
          // email is null when driver is not assigned, so use driver name as id
          name: driver.name,
          totalRideCount: driver.total_ride_count,
          trackedRideCount: driver.tracked_ride_count
        })
      ),
      totalPageCount: Math.ceil(data.count / pageSize)
    };
  }
);
const getRideTrackingSummary = f$i.fetcher(
  async ({ ctx, get }, { startDate, endDate }) => {
    const { driverTrackingUrl } = urlConfig$j[ctx.env];
    const params = {
      start_date: startDate.toISOString().split("T")[0],
      end_date: endDate.toISOString().split("T")[0]
    };
    const paramsString = new URLSearchParams(params).toString();
    const { data } = await get(
      `${driverTrackingUrl}/pie-chart/?${paramsString}`,
      z.object({
        total_ride_count: z.number(),
        tracked_ride_count: z.number()
      })
    );
    return {
      trackedRideCount: data.tracked_ride_count,
      untrackedRideCount: data.total_ride_count - data.tracked_ride_count
    };
  }
);
const getVehicles = f$i.fetcher(
  async ({ ctx, get }, { page }) => {
    const PAGE_SIZE = 5;
    const { getVehiclesUrl } = urlConfig$j[ctx.env];
    const { data } = await get(
      `${getVehiclesUrl}?page_size=${PAGE_SIZE}&page=${page}`,
      z.object({
        count: z.number(),
        results: z.array(
          z.object({
            id: z.number(),
            manufacturer: z.string(),
            model: z.string(),
            year: z.string(),
            plate: z.string(),
            color: z.string()
          })
        )
      })
    );
    return {
      vehicles: data.results,
      totalPageCount: Math.ceil(data.count || 0 / PAGE_SIZE)
    };
  }
);
const getDriverRidesLinks = f$i.fetcher(
  async ({ ctx, get }, { driverId, startDate, endDate, tracked }) => {
    if (driverId === "NO DRIVER ASSIGNED") {
      return [];
    }
    const { driverUntrackedRidesUrl, driverTrackedRidesUrl } = urlConfig$j[ctx.env];
    const url = tracked ? driverTrackedRidesUrl : driverUntrackedRidesUrl;
    const params = {
      driver_email: driverId,
      start_date: startDate.toISOString().split("T")[0],
      end_date: endDate.toISOString().split("T")[0]
    };
    const paramsString = new URLSearchParams(params).toString();
    const { data } = await get(
      `${url}?${paramsString}`,
      z.object({
        reservations: z.array(
          z.object({
            reservation_hashed_id: z.string(),
            user_external_id: z.string()
          })
        )
      })
    );
    return data.reservations.map(
      (result) => ({
        id: result.reservation_hashed_id,
        content: result.user_external_id
      })
    );
  }
);
const fetchProviderInfoHelper = async ({ ctx, get }, token) => {
  const { data } = await get(
    `${urlConfig$j[ctx.env].byDriverUrl}`,
    z.object({
      id: z.number(),
      uid: z.string(),
      name: z.string()
    }),
    {
      headers: {
        Authorization: `JWT ${token}`
      }
    }
  );
  return {
    providerId: data.id,
    providerUid: data.uid,
    providerName: data.name
  };
};
const updateUserProviderHelper = async ({ ctx, post }, providerId, token) => {
  await post(
    `${urlConfig$j[ctx.env].updateUserProviderUrl}`,
    z.void(),
    { provider_uid: providerId },
    {
      headers: {
        Authorization: `JWT ${token}`
      }
    }
  );
};
const getProviders = f$i.fetcher(
  async ({ ctx, get }, { search = "" }) => {
    const { providersUrl } = urlConfig$j[ctx.env];
    const params = new URLSearchParams({
      ordering: "name",
      ...search ? { search } : {}
    }).toString();
    const { data } = await get(
      `${providersUrl}?${params}`,
      z.object({
        results: z.array(
          z.object({
            uid: z.string(),
            name: z.string()
          })
        )
      })
    );
    return {
      providers: data.results
    };
  }
);
const createProviderQuery = f$i.combineFetchers({
  getDriversRides,
  getRideTrackingSummary,
  getVehicles,
  getDriverRidesLinks,
  updateUserProviderHelper,
  getProviders
});

const urlConfig$i = {
  staging: {
    authUrl: "https://api-staging-internal.mozio.com/v2/auth"
  },
  production: {
    authUrl: "https://api.mozio.com/v2/auth"
  },
  testing: {
    authUrl: "https://api-testing.mozio.com/v2/auth"
  }
};
const f$h = new FetcherClient({
  ctx: z.object({
    env: envSchema
  })
});
const partnersDashboardAuthF = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    "api-key": z.string()
  }
});
const ERROR_CODES$1 = {
  REFRESH_TOKEN_FAILED: "REFRESH_TOKEN_FAILED",
  INVALID_REFRESH_TOKEN: "INVALID_REFRESH_TOKEN"
};
class AuthError extends Error {
  constructor(message) {
    super(
      message ? `Authentication failed: ${message}` : "Authentication failed"
    );
  }
}
const authErrorSchema = z.object({
  code: z.union([
    z.literal("authentication_failed"),
    z.literal("not_authenticated"),
    z.literal("permission_denied")
  ]),
  message: z.string().min(1).optional().nullable()
});
const refreshTokensDataSchema = z.union([
  z.object({
    token: z.string().min(1),
    refresh_token: z.string().min(1)
  }),
  z.object({
    refresh_token: z.array(
      z.object({
        code: z.string().min(1),
        message: z.string().min(1)
      })
    )
  })
]);
const refreshTokens = f$h.fetcher(
  async ({ ctx, post }, refreshToken) => {
    const { data } = await post(
      `${urlConfig$i[ctx.env].authUrl}/token-refresh/`,
      refreshTokensDataSchema,
      { refresh_token: refreshToken }
    );
    if ("token" in data) {
      return { accessToken: data.token, refreshToken: data.refresh_token };
    }
    return {
      error: ERROR_CODES$1.REFRESH_TOKEN_FAILED,
      message: data.refresh_token[0]?.message
    };
  }
);
const tokenBlacklistSchema = z.union([
  z.object({
    refresh_token: z.string().min(1)
  }),
  z.object({
    refresh_token: z.array(
      z.object({
        code: z.string().min(1),
        message: z.string().min(1)
      })
    )
  })
]);
const tokenBlacklist = f$h.fetcher(
  async ({ ctx, post }, input) => {
    const { data } = await post(
      `${urlConfig$i[ctx.env].authUrl}/token-blacklist/`,
      tokenBlacklistSchema,
      {
        refresh_token: input.refreshToken
      }
    );
    if (!Array.isArray(data.refresh_token)) {
      return { refreshToken: data.refresh_token };
    }
    return {
      error: ERROR_CODES$1.INVALID_REFRESH_TOKEN,
      message: data.refresh_token[0]?.message
    };
  }
);
const providersDashboardLogin = f$h.fetcher(
  async ({ ctx, post }, userData) => {
    const loginEndpoint = "/drivers/token";
    const { data: response } = await post(
      `${urlConfig$i[ctx.env].authUrl}${loginEndpoint}`,
      z.object({
        token: z.string(),
        refresh_token: z.string().optional(),
        data: z.object({
          email: z.string(),
          first_name: z.string(),
          last_name: z.string(),
          is_staff: z.boolean()
        })
      }),
      userData
    );
    return {
      token: response.token,
      refreshToken: response.refresh_token,
      data: {
        email: response.data.email,
        firstName: response.data.first_name,
        lastName: response.data.last_name,
        isStaff: response.data.is_staff
      }
    };
  }
);
const partnersDashboardLogin = partnersDashboardAuthF.fetcher(
  async ({ ctx, post }, userData) => {
    const loginEndpoint = "/token";
    const { data: response } = await post(
      `${urlConfig$i[ctx.env].authUrl}${loginEndpoint}`,
      z.object({
        token: z.string(),
        refresh_token: z.string().optional(),
        data: z.object({
          email: z.string(),
          first_name: z.string(),
          last_name: z.string(),
          is_staff: z.boolean()
        })
      }),
      userData
    );
    return {
      token: response.token,
      refreshToken: response.refresh_token,
      data: {
        email: response.data.email,
        firstName: response.data.first_name,
        lastName: response.data.last_name,
        isStaff: response.data.is_staff
      }
    };
  }
);
const partnersDashboardRefreshTokens = partnersDashboardAuthF.fetcher(
  async ({ ctx, post }, refreshToken) => {
    const { data } = await post(
      `${urlConfig$i[ctx.env].authUrl}/token-refresh/`,
      refreshTokensDataSchema,
      { refresh_token: refreshToken }
    );
    if ("token" in data) {
      return { accessToken: data.token, refreshToken: data.refresh_token };
    }
    return {
      error: ERROR_CODES$1.REFRESH_TOKEN_FAILED,
      message: data.refresh_token[0]?.message
    };
  }
);
const partnersDashboardTokenBlacklist = partnersDashboardAuthF.fetcher(
  async ({ ctx, post }, input) => {
    const { data } = await post(
      `${urlConfig$i[ctx.env].authUrl}/token-blacklist/`,
      tokenBlacklistSchema,
      {
        refresh_token: input.refreshToken
      }
    );
    if (!Array.isArray(data.refresh_token)) {
      return { refreshToken: data.refresh_token };
    }
    return {
      error: ERROR_CODES$1.INVALID_REFRESH_TOKEN,
      message: data.refresh_token[0]?.message
    };
  }
);
const getProviderInfo = f$h.fetcher(fetchProviderInfoHelper);
const updateUserProvider = f$h.fetcher(
  async ({ ctx, post }, { providerId, token }) => {
    try {
      return await updateUserProviderHelper({ ctx, post }, providerId, token);
    } catch (error) {
      if (error instanceof SyntaxError && error.message.includes("JSON")) {
        return;
      }
      throw error;
    }
  }
);
const loginF = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    "api-key": z.string()
  }
});
const login = loginF.fetcher(
  async ({ ctx, post }, input) => {
    const { data } = await post(
      `${urlConfig$i[ctx.env].authUrl}/token`,
      z.object({
        token: z.string(),
        refresh_token: z.string().optional()
      }),
      input
    );
    return {
      token: data.token,
      refreshToken: data.refresh_token ?? ""
    };
  }
);
const createLoginQuery = loginF.combineFetchers({
  login
});
const createAuthQuery = f$h.combineFetchers({
  refreshTokens,
  tokenBlacklist
});
const createProviderDashboardAuthQuery = f$h.combineFetchers({
  login: providersDashboardLogin,
  getProviderInfo,
  updateUserProvider,
  refreshTokens,
  tokenBlacklist
});
const createPartnersDashboardAuthQuery = partnersDashboardAuthF.combineFetchers({
  login: partnersDashboardLogin,
  refreshTokens: partnersDashboardRefreshTokens,
  tokenBlacklist: partnersDashboardTokenBlacklist
});

const urlConfig$h = {
  staging: {
    esimUrl: "https://api-staging-internal.mozio.com/travel-extras/esim",
    paymentUrl: "https://api-staging-internal.mozio.com/travel-extras/esim"
  },
  production: {
    esimUrl: "https://api.mozio.com/travel-extras/esim",
    paymentUrl: "https://api.mozio.com/travel-extras/esim"
  },
  testing: {
    esimUrl: "https://api-testing.mozio.com/travel-extras/esim",
    paymentUrl: "https://api-testing.mozio.com/travel-extras/esim"
  }
};
const f$g = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    lang: z.string(),
    "x-whitelabel": z.string(),
    "white-label-id": z.number().optional(),
    "white-label-name": z.string(),
    "partner-ref": z.string(),
    hostname: z.string(),
    "distinct-id": z.union([z.string(), z.number()]).optional(),
    Authorization: z.string().optional(),
    "api-key": z.string()
  }
});
const getEsimCountries = f$g.fetcher(async ({ ctx, get }) => {
  const { esimUrl } = urlConfig$h[ctx.env];
  const { data } = await get(
    `${esimUrl}/country/`,
    z.array(
      z.object({
        name: z.string().min(1),
        code: z.string().min(1)
      })
    )
  );
  return data;
});
const planSchema = z.array(
  z.object({
    product_id: z.string().min(1),
    provider: z.string().min(1),
    data_gb: z.string().min(1),
    validity_days: z.number(),
    price: z.object({
      currency: z.string().min(1),
      amount: z.number(),
      symbol: z.string().min(1)
    })
  })
);
const filterIdenticalPlans = (plans) => {
  const identicalPlanMap = /* @__PURE__ */ new Map();
  plans.forEach((plan) => {
    const identicalBy = `${plan.data_gb}${plan.validity_days}`;
    const price = identicalPlanMap.get(identicalBy)?.price.amount ?? Number.POSITIVE_INFINITY;
    if (plan.price.amount < price) {
      identicalPlanMap.set(identicalBy, plan);
    }
  });
  return [...identicalPlanMap.values()];
};
const getEsimPlans = f$g.fetcher(
  async ({ ctx, post }, { countryCode, currency }) => {
    const { esimUrl } = urlConfig$h[ctx.env];
    const { data } = await post(`${esimUrl}/search/`, planSchema, {
      country_code: countryCode,
      currency
    });
    const uniquePlans = filterIdenticalPlans(data);
    return uniquePlans.map(
      (plan) => ({
        id: plan.product_id,
        value: plan.product_id,
        dataGb: plan.data_gb,
        price: plan.price,
        validityDays: plan.validity_days,
        // NOTE: `metadata` is used to represent non-front-end model data returned solely for subsequent API calls.
        metadata: {
          purchasePayload: {
            esim_id: plan.product_id,
            provider: plan.provider,
            price: plan.price,
            country_code: countryCode
          }
        }
      })
    );
  }
);
const PURCHASE_ESIM_STATUS = {
  SUCCESS: "SUCCESS",
  REQUIRE_CHECKOUT: "REQUIRE_CHECKOUT"
};
const purchaseSchema = z.union([
  z.object({
    status: z.enum([
      PURCHASE_ESIM_STATUS.SUCCESS,
      PURCHASE_ESIM_STATUS.REQUIRE_CHECKOUT
    ]),
    checkout_url: z.string().url().nullable(),
    price: z.object({
      updated: z.boolean(),
      value: z.number().nullable()
    })
  }),
  authErrorSchema
]);
const purchaseEsim = f$g.fetcher(
  async ({ ctx, post }, {
    couponCode,
    purchasePayload,
    purchaseSuccessUrl,
    purchaseFailUrl
  }) => {
    const { paymentUrl } = urlConfig$h[ctx.env];
    const { data } = await post(`${paymentUrl}/checkout/`, purchaseSchema, {
      ...purchasePayload,
      coupon_code: couponCode,
      success_url: purchaseSuccessUrl,
      cancel_url: purchaseFailUrl
    });
    if ("code" in data) {
      throw new AuthError();
    }
    if (data.status === PURCHASE_ESIM_STATUS.SUCCESS) {
      return {
        status: PURCHASE_ESIM_STATUS.SUCCESS
      };
    }
    if (!data.checkout_url) {
      throw new Error("No checkout url");
    }
    return {
      status: data.status,
      checkoutUrl: data.checkout_url,
      newPrice: data.price.updated && typeof data.price.value === "number" ? data.price.value : void 0
    };
  }
);
const DISCOUNT_TYPE = {
  FIXED: "FIXED",
  PERCENTAGE: "PERCENTAGE"
};
const validateCoupon = f$g.fetcher(
  async ({ ctx, post }, { couponCode, currency, esimId }) => {
    const { esimUrl } = urlConfig$h[ctx.env];
    const { data } = await post(
      `${esimUrl}/coupon/`,
      z.union([
        z.object({
          coupon_code: z.string(),
          esim_id: z.string(),
          discount: z.object({
            type: z.enum([DISCOUNT_TYPE.FIXED, DISCOUNT_TYPE.PERCENTAGE]),
            value: z.number()
          }),
          discounted_price: z.object({
            amount: z.number(),
            currency: z.string(),
            symbol: z.string()
          })
        }),
        authErrorSchema
      ]),
      {
        coupon_code: couponCode,
        currency,
        esim_id: esimId
      }
    );
    if ("code" in data) {
      throw new AuthError();
    }
    return {
      couponCode: data.coupon_code,
      esimId: data.esim_id,
      discount: {
        type: data.discount.type,
        value: data.discount.value
      },
      discountedPrice: {
        amount: data.discounted_price.amount,
        currency: data.discounted_price.currency,
        symbol: data.discounted_price.symbol
      }
    };
  }
);
const createEsimQuery = f$g.combineFetchers({
  getEsimCountries,
  getEsimPlans,
  purchaseEsim,
  validateCoupon
});

const nonAuthFetcher = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    "api-key": z.string()
  }
});
const authFetcher = new FetcherClient({
  ctx: z.object({
    env: envSchema,
    reservationHashId: z.string()
  }),
  headers: {
    authorization: z.string(),
    "api-key": z.string()
  }
});
const apiTripStatusSchema = z.enum([
  "cancelled",
  "not_started",
  "driver_departed_to_pickup",
  "driver_arrived_at_pickup",
  "driver_departed_to_dropoff",
  "driver_arrived_at_dropoff",
  "no_show_reported_by_driver"
]).optional().default("not_started");
const DRIVER_TRIP_STATUS = {
  CANCELLED: "cancelled",
  NOT_STARTED: "notStarted",
  DRIVING_TO_PICK_UP: "drivingToPickUp",
  ARRIVED_AT_PICK_UP: "arrivedAtPickUp",
  DRIVING_TO_DROP_OFF: "drivingToDropOff",
  ARRIVED_AT_DROP_OFF: "arrivedAtDropOff",
  NO_SHOW: "noShow"
};
const urlConfig$g = {
  staging: {
    tripUrl: "https://api-staging-internal.mozio.com/v2/data-entry/vehicle-tracking/reservations",
    changeStatusUrl: "https://api-staging-internal.mozio.com/v2/data-entry/vehicle-tracking/reservations",
    authUrl: "https://api-staging-internal.mozio.com/v2/auth/drivers/sso-login/",
    tripUrlV2: "https://api-staging-internal.mozio.com/v2/reservations",
    changeStatusUrlV2: "https://tracking-staging.mozio.com/v1/trackings",
    directionsUrlV2: "https://api-staging-internal.mozio.com/v2/locations/directions/",
    latestTrackingEventUrlV2: "https://tracking-staging.mozio.com/v1/trackings"
  },
  production: {
    tripUrl: "https://api.mozio.com/v2/data-entry/vehicle-tracking/reservations",
    changeStatusUrl: "https://api.mozio.com/v2/data-entry/vehicle-tracking/reservations",
    authUrl: "https://api.mozio.com/v2/auth/drivers/sso-login/",
    tripUrlV2: "https://api.mozio.com/v2/reservations",
    changeStatusUrlV2: "https://tracking-api.mozio.com/v1/trackings",
    directionsUrlV2: "https://api.mozio.com/v2/locations/directions/",
    latestTrackingEventUrlV2: "https://tracking-api.mozio.com/v1/trackings"
  },
  testing: {
    tripUrl: "https://api-testing.mozio.com/v2/data-entry/vehicle-tracking/reservations",
    changeStatusUrl: "https://api-testing.mozio.com/v2/data-entry/vehicle-tracking/reservations",
    authUrl: "https://api-testing.mozio.com/v2/auth/drivers/sso-login/",
    tripUrlV2: "https://api-testing.mozio.com/v2/reservations",
    changeStatusUrlV2: "https://tracking-api-testing.mozio.com/v1/trackings",
    directionsUrlV2: "https://api-testing.mozio.com/v2/locations/directions/",
    latestTrackingEventUrlV2: "https://tracking-api-testing.mozio.com/v1/trackings"
  }
};
const getNextStatus = (currentStatus) => {
  if (currentStatus === DRIVER_TRIP_STATUS.NO_SHOW || currentStatus === DRIVER_TRIP_STATUS.ARRIVED_AT_DROP_OFF) {
    throw new Error(`You cannot update '${currentStatus}' status.`);
  }
  const nextStatus = {
    cancelled: DRIVER_TRIP_STATUS.CANCELLED,
    notStarted: DRIVER_TRIP_STATUS.DRIVING_TO_PICK_UP,
    drivingToPickUp: DRIVER_TRIP_STATUS.ARRIVED_AT_PICK_UP,
    arrivedAtPickUp: DRIVER_TRIP_STATUS.DRIVING_TO_DROP_OFF,
    drivingToDropOff: DRIVER_TRIP_STATUS.ARRIVED_AT_DROP_OFF
  };
  return nextStatus[currentStatus];
};
const mapApiTripStatusToTripStatus = (status) => {
  const map = {
    cancelled: DRIVER_TRIP_STATUS.CANCELLED,
    not_started: DRIVER_TRIP_STATUS.NOT_STARTED,
    driver_departed_to_pickup: DRIVER_TRIP_STATUS.DRIVING_TO_PICK_UP,
    driver_arrived_at_pickup: DRIVER_TRIP_STATUS.ARRIVED_AT_PICK_UP,
    driver_departed_to_dropoff: DRIVER_TRIP_STATUS.DRIVING_TO_DROP_OFF,
    driver_arrived_at_dropoff: DRIVER_TRIP_STATUS.ARRIVED_AT_DROP_OFF,
    no_show_reported_by_driver: DRIVER_TRIP_STATUS.NO_SHOW
  };
  return map[status];
};
const mapTripStatusToApiTripStatus = (tripStatus) => {
  const map = {
    [DRIVER_TRIP_STATUS.CANCELLED]: "cancelled",
    [DRIVER_TRIP_STATUS.NOT_STARTED]: "not_started",
    [DRIVER_TRIP_STATUS.DRIVING_TO_PICK_UP]: "driver_departed_to_pickup",
    [DRIVER_TRIP_STATUS.ARRIVED_AT_PICK_UP]: "driver_arrived_at_pickup",
    [DRIVER_TRIP_STATUS.DRIVING_TO_DROP_OFF]: "driver_departed_to_dropoff",
    [DRIVER_TRIP_STATUS.ARRIVED_AT_DROP_OFF]: "driver_arrived_at_dropoff",
    [DRIVER_TRIP_STATUS.NO_SHOW]: "no_show_reported_by_driver"
  };
  return map[tripStatus];
};
const getNavigationDestination = (status, departureCoords, destinationCoords) => {
  const map = {
    [DRIVER_TRIP_STATUS.CANCELLED]: void 0,
    [DRIVER_TRIP_STATUS.NOT_STARTED]: void 0,
    [DRIVER_TRIP_STATUS.DRIVING_TO_PICK_UP]: departureCoords,
    [DRIVER_TRIP_STATUS.ARRIVED_AT_PICK_UP]: void 0,
    [DRIVER_TRIP_STATUS.DRIVING_TO_DROP_OFF]: destinationCoords,
    [DRIVER_TRIP_STATUS.ARRIVED_AT_DROP_OFF]: void 0,
    [DRIVER_TRIP_STATUS.NO_SHOW]: void 0
  };
  return map[status];
};
const getDirectionsV2$1 = authFetcher.fetcher(
  async ({ post, ctx }, { category, start, end }) => {
    const { data } = await post(
      urlConfig$g[ctx.env].directionsUrlV2,
      z.object({
        segments: z.array(
          z.object({
            category: z.string(),
            polyline_points: z.string(),
            distance: z.number(),
            duration: z.number()
          })
        )
      }),
      {
        segments: [
          {
            category,
            start,
            end
          }
        ]
      }
    );
    const segments = data.segments.map((segment) => {
      return {
        category: segment.category,
        polyline_points: segment.polyline_points,
        distance: segment.distance,
        duration: segment.duration
      };
    });
    return {
      segments
    };
  }
);
const getLatestTrackingEventV2 = authFetcher.fetcher(
  async ({ ctx, get }) => {
    const { env, reservationHashId } = ctx;
    const { data, response } = await get(
      `${urlConfig$g[env].latestTrackingEventUrlV2}/${reservationHashId}/events/latest`,
      z.union([
        z.object({
          status: apiTripStatusSchema
        }),
        z.object({
          detail: z.string()
        })
      ])
    );
    if (response.status === 404) {
      return {
        status: DRIVER_TRIP_STATUS.NOT_STARTED
      };
    }
    if ("detail" in data) {
      return {
        status: DRIVER_TRIP_STATUS.NOT_STARTED
      };
    }
    const status = mapApiTripStatusToTripStatus(data.status);
    return {
      status
    };
  }
);
const updateToNextStatusV2 = authFetcher.fetcher(
  async ({ ctx, post }, { currentStatus, position }) => {
    const { env, reservationHashId } = ctx;
    const { latitude: lat, longitude: lng } = position || {};
    const nextStatus = mapTripStatusToApiTripStatus(
      getNextStatus(currentStatus)
    );
    const { data } = await post(
      `${urlConfig$g[env].changeStatusUrlV2}/${reservationHashId}/events`,
      z.object({
        status: apiTripStatusSchema
      }),
      {
        status: nextStatus,
        ...position ? {
          coordinates: {
            lat,
            lng
          }
        } : {
          coordinates: {
            lat: 0,
            lng: 0
          }
        }
      }
    );
    return {
      status: mapApiTripStatusToTripStatus(data.status)
    };
  }
);
const getTripV2 = authFetcher.fetcher(async ({ ctx, get }) => {
  const { env, reservationHashId } = ctx;
  const amenitySchema = z.object({
    key: z.string(),
    name: z.string(),
    image_url: z.string().url(),
    included: z.boolean()
  });
  const { data } = await get(
    `${urlConfig$g[env].tripUrlV2}/${reservationHashId}`,
    z.object({
      id: z.string(),
      timestamp: z.string(),
      confirmation_number: z.string(),
      pickup_instructions: z.string(),
      first_name: z.string(),
      last_name: z.string(),
      phone_number: z.string(),
      voyage: z.object({
        num_passengers: z.number(),
        amenities: z.array(amenitySchema),
        departure_datetime: z.string(),
        arrival_datetime: z.string(),
        provider_name: z.string(),
        start_location: z.object({
          formatted_address: z.string().min(1),
          lat: z.number(),
          lng: z.number()
        }),
        end_location: z.object({
          formatted_address: z.string().min(1),
          lat: z.number(),
          lng: z.number()
        }),
        booking_details: z.object({
          notes: z.string(),
          wait_time: z.number()
        }),
        vehicle: z.object({
          image_url: z.string(),
          max_bags: z.number(),
          max_passengers: z.number(),
          total_bags: z.number(),
          vehicle_class_detail: z.object({
            display_name: z.string()
          })
        })
      })
    })
  );
  const amenities = data.voyage.amenities.map((amenity) => {
    return {
      key: amenity.key,
      name: amenity.name,
      included: amenity.included,
      imageUrl: amenity.image_url
    };
  });
  return {
    amenities,
    id: data.id,
    timestamp: data.timestamp,
    confirmationNumber: data.confirmation_number,
    pickupInstructions: data.pickup_instructions,
    waitTime: data.voyage.booking_details.wait_time,
    providerName: data.voyage.provider_name,
    from: {
      location: data.voyage.start_location.formatted_address
    },
    to: {
      location: data.voyage.end_location.formatted_address
    },
    endLocation: {
      latitude: data.voyage.start_location.lat,
      longitude: data.voyage.start_location.lng
    },
    customerNotes: data.voyage.booking_details.notes,
    arrivalDatetime: data.voyage.arrival_datetime,
    departureDatetime: data.voyage.departure_datetime,
    phone: data.phone_number,
    vehicle: {
      imageUrl: data.voyage.vehicle.image_url,
      maxBags: data.voyage.vehicle.max_bags,
      maxPassengers: data.voyage.vehicle.max_passengers,
      vehicleClass: data.voyage.vehicle.vehicle_class_detail.display_name
    },
    passengerCardProps: {
      hasCustomerNotes: !!data.voyage.booking_details.notes,
      name: `${data.first_name} ${data.last_name}`,
      passengerCount: data.voyage.num_passengers,
      luggageCount: data.voyage.vehicle.total_bags ?? void 0
    }
  };
});
const getTrip = authFetcher.fetcher(async ({ ctx, get }) => {
  const { env, reservationHashId } = ctx;
  const { data } = await get(
    `${urlConfig$g[env].tripUrl}/${reservationHashId}`,
    z.object({
      passenger_information: z.object({
        first_name: z.string().min(1),
        last_name: z.string().min(1),
        phone: z.string().min(1)
      }),
      num_passengers: z.number().min(1),
      num_bags: z.number().min(1).nullable(),
      start_location: z.object({
        full_address: z.string().min(1),
        lat: z.number(),
        lng: z.number()
      }),
      departure_datetime: z.string().min(1),
      end_location: z.object({
        full_address: z.string().min(1),
        lat: z.number(),
        lng: z.number()
      }),
      arrival_datetime: z.string().min(1).nullable(),
      flight_info: z.object({
        flight_code: z.string().min(1)
      }).nullable(),
      driver: z.object({
        user_external_id: z.number()
      }),
      status: apiTripStatusSchema,
      customer_notes: z.string().nullable()
    })
  );
  const status = mapApiTripStatusToTripStatus(data.status);
  const navigationDestination = getNavigationDestination(
    status,
    { latitude: data.start_location.lat, longitude: data.start_location.lng },
    { latitude: data.end_location.lat, longitude: data.end_location.lng }
  );
  return {
    from: {
      location: data.start_location.full_address,
      time: new Date(data.departure_datetime)
    },
    to: {
      location: data.end_location.full_address,
      time: data.arrival_datetime ? new Date(data.arrival_datetime) : void 0
    },
    flight: data.flight_info?.flight_code ?? void 0,
    status,
    navigationDestination,
    customerNotes: data.customer_notes ?? void 0,
    phone: data.passenger_information.phone,
    driver: {
      id: data.driver.user_external_id
    },
    passengerCardProps: {
      hasCustomerNotes: !!data.customer_notes,
      name: `${data.passenger_information.first_name} ${data.passenger_information.last_name}`,
      passengerCount: data.num_passengers,
      luggageCount: data.num_bags ?? void 0
    }
  };
});
const updateToNextStatus = authFetcher.fetcher(
  async ({ ctx, post }, { currentStatus, position }) => {
    const { changeStatusUrl } = urlConfig$g[ctx.env];
    const { latitude: lat, longitude: lng } = position || {};
    const nextStatus = getNextStatus(currentStatus);
    const { data } = await post(
      `${changeStatusUrl}/${ctx.reservationHashId}/change-status/`,
      z.union([
        z.object({
          status: apiTripStatusSchema
        }),
        z.object({
          non_field_errors: z.array(
            z.object({
              message: z.string()
            })
          ).min(1)
        })
      ]),
      {
        status: mapTripStatusToApiTripStatus(nextStatus),
        ...position ? {
          position: {
            lat,
            lng
          }
        } : {}
      }
    );
    if ("non_field_errors" in data) {
      return {
        userError: data.non_field_errors[0].message
      };
    }
    if (nextStatus === mapApiTripStatusToTripStatus(data.status)) {
      return { status: nextStatus };
    }
    throw new Error("Returned status don't match with sent one.");
  }
);
const cancelTrip = authFetcher.fetcher(
  async ({ ctx, post }, { position }) => {
    const { changeStatusUrl } = urlConfig$g[ctx.env];
    const { latitude: lat, longitude: lng } = position || {};
    const {
      data: { status: returnedStatus }
    } = await post(
      `${changeStatusUrl}/${ctx.reservationHashId}/change-status/`,
      z.object({
        status: apiTripStatusSchema
      }),
      {
        status: mapTripStatusToApiTripStatus(DRIVER_TRIP_STATUS.NO_SHOW),
        ...position ? {
          position: {
            lat,
            lng
          }
        } : {}
      }
    );
    if (DRIVER_TRIP_STATUS.NO_SHOW === mapApiTripStatusToTripStatus(returnedStatus)) {
      return DRIVER_TRIP_STATUS.NO_SHOW;
    }
    throw new Error("Returned status don't match with sent one.");
  }
);
const cancelTripV2 = authFetcher.fetcher(
  async ({ ctx, post }, { position }) => {
    const { env, reservationHashId } = ctx;
    const { latitude: lat, longitude: lng } = position || {};
    const {
      data: { status: returnedStatus }
    } = await post(
      `${urlConfig$g[env].changeStatusUrlV2}/${reservationHashId}/events`,
      z.object({
        status: apiTripStatusSchema
      }),
      {
        status: mapTripStatusToApiTripStatus(DRIVER_TRIP_STATUS.NO_SHOW),
        ...position ? {
          position: {
            lat,
            lng
          }
        } : {}
      }
    );
    if (DRIVER_TRIP_STATUS.NO_SHOW === mapApiTripStatusToTripStatus(returnedStatus)) {
      return DRIVER_TRIP_STATUS.NO_SHOW;
    }
    throw new Error("Returned status don't match with sent one.");
  }
);
const getToken = nonAuthFetcher.fetcher(
  async ({ ctx, post }, { ssoToken }) => {
    const { data } = await post(
      urlConfig$g[ctx.env].authUrl,
      z.union([
        z.object({
          key: z.array(z.object({ code: z.literal("invalid") }))
        }),
        z.object({
          refresh_token: z.string(),
          token: z.string(),
          provider_name: z.string()
        })
      ]),
      {
        key: ssoToken
      }
    );
    if ("key" in data && data.key[0] && "code" in data.key[0]) {
      return {
        error: data.key[0].code
      };
    }
    if ("token" in data) {
      return {
        providerName: data.provider_name,
        token: data.token
      };
    }
    throw new Error(
      "An unexpected error occurred during the authentication process. Please try again later."
    );
  }
);
const createDriverQuery = nonAuthFetcher.combineFetchers({
  getToken
});
const createAuthDriverQuery = authFetcher.combineFetchers({
  getTrip,
  updateToNextStatus,
  cancelTrip,
  getDirectionsV2: getDirectionsV2$1,
  getLatestTrackingEventV2,
  updateToNextStatusV2,
  getTripV2,
  cancelTripV2
});

const urlConfig$f = {
  production: {
    whiteLabelUrl: "https://api.mozio.com/v2/whitelabels"
  },
  testing: {
    whiteLabelUrl: "https://api-testing.mozio.com/v2/whitelabels"
  },
  staging: {
    whiteLabelUrl: "https://api-staging-internal.mozio.com/v2/whitelabels"
  }
};
const f$f = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    "x-access-token": z.string()
  }
});
const getInjectContent = ({
  metadata,
  themeVars,
  config
}) => {
  const {
    title,
    description,
    keywords,
    imageUrl,
    faviconUrl,
    canonicalUrl,
    url
  } = metadata;
  return `
  <title>${title}</title>
  <meta name="description" content="${description}">
  <meta name="keywords" content="${keywords}">
  <meta name="og:title" content="${title}">
  <meta name="og:description" content="${description}">
  <meta name="og:url" content="${url}">
  <meta name="og:image" content="${imageUrl}">
  <meta property="og:image" content="${imageUrl}">
  <link rel="icon" type="image/x-icon" href="${faviconUrl}">
  <link rel="canonical" href="${canonicalUrl}">
  <style>
    :root {
      ${Object.entries(themeVars).map(([key, value]) => `${key}: ${value};`).join(" ")}
    }
  </style>
  <script>
    window.WHITE_LABEL_CONFIG = ${JSON.stringify(config)};
  <\/script>
  `;
};
const colorKeySchema = z.enum([
  "white",
  "black",
  "primary-100",
  "primary-200",
  "primary-300",
  "primary-400",
  "primary-500",
  "primary-contrast",
  "secondary-100",
  "secondary-200",
  "secondary-300",
  "secondary-400",
  "secondary-500",
  "secondary-contrast",
  "neutral-100",
  "neutral-200",
  "neutral-300",
  "neutral-400",
  "neutral-500",
  "neutral-600",
  "neutral-700",
  "neutral-800",
  "neutral-900",
  "success-100",
  "success-200",
  "success-300",
  "success-400",
  "success-500",
  "error-100",
  "error-200",
  "error-300",
  "error-400",
  "error-500",
  "warning-100",
  "warning-200",
  "warning-300",
  "warning-400",
  "warning-500"
]);
const configSchema = z.object({
  NAME: z.string().min(1),
  ID: z.number(),
  PARTNER_REF: z.string(),
  FIRST_BACKGROUND_COLOR: z.string().min(1),
  SECOND_BACKGROUND_COLOR: z.string().min(1),
  THIRD_BACKGROUND_COLOR: z.string().min(1),
  FOURTH_BACKGROUND_COLOR: z.string().min(1),
  CTA_BACKGROUND_COLOR: z.string().min(1),
  FIRST_CONTRAST_COLOR: z.string().min(1),
  FOURTH_CONTRAST_COLOR: z.string().min(1),
  CTA_CONTRAST_COLOR: z.string().min(1),
  HEADER_CUSTOM_HEIGHT: z.string().min(1),
  HEADER_CUSTOM_LOGO_HEIGHT: z.string(),
  HEADER_BG: colorKeySchema,
  HEADER_BORDER_COLOR: colorKeySchema,
  HEADER_CREATE_ACCOUNT_BTN_BG: colorKeySchema,
  HEADER_CREATE_ACCOUNT_BTN_TEXT: colorKeySchema,
  HEADER_CREATE_ACCOUNT_BTN_HOVER_BG: colorKeySchema,
  HEADER_CREATE_ACCOUNT_BTN_HOVER_TEXT: colorKeySchema,
  HEADER_SIGN_IN_BTN_BG: colorKeySchema,
  HEADER_SIGN_IN_BTN_TEXT: colorKeySchema,
  HEADER_SIGN_IN_BTN_HOVER_BG: colorKeySchema,
  HEADER_SIGN_IN_BTN_HOVER_TEXT: colorKeySchema,
  HEADER_GENERAL_BTN_BG: colorKeySchema,
  HEADER_GENERAL_BTN_TEXT: colorKeySchema,
  HEADER_GENERAL_BTN_HOVER_BG: colorKeySchema,
  HEADER_GENERAL_BTN_HOVER_TEXT: colorKeySchema,
  DEFAULT_CURRENCY: z.string(),
  AUTODETECT_CURRENCY_BY_IP: z.boolean(),
  FOOTER_SOCIALS: z.array(
    z.tuple([
      z.enum(["facebook", "twitter", "instagram", "linkedin", ""]),
      z.string()
    ])
  ),
  CUSTOM_SOCIAL_LOGO: z.string(),
  CUSTOM_LOGO: z.string(),
  CUSTOM_FOOTER_LOGO: z.string().nullable(),
  CUSTOM_FOOTER_HTML: z.string().nullable(),
  SUPPORT_PHONE_NUMBERS: z.array(z.tuple([z.string(), z.string()])).nullable(),
  SHOW_LEGAL_ADDRESS_IN_FOOTER: z.boolean(),
  LOYALTY_PROGRAM_ENABLED: z.boolean(),
  LOYALTY_PROGRAM_NAME: z.string().nullable(),
  CUSTOM_BOOKING_DOMAIN: z.string().nullable().optional(),
  SUPPORT_FORM_LINK: z.string().nullable(),
  FOOTER_TERMS_LINKS: z.array(z.tuple([z.string(), z.string()])).nullable(),
  FOOTER_CUSTOM_LINKS: z.array(z.tuple([z.string(), z.string()])).nullable(),
  CUSTOM_DOMAINS: z.array(z.tuple([z.string(), z.string()])),
  CUSTOM_FAVICON: z.string(),
  CUSTOM_HEADER_TITLE: z.array(z.tuple([z.string(), z.string()])).optional(),
  CUSTOM_HEADER_DESCRIPTION: z.array(z.tuple([z.string(), z.string()])).optional(),
  CUSTOM_HEADER_KEYWORDS: z.array(z.tuple([z.string(), z.string()])).optional(),
  EMAIL_SITE_NAME: z.string().optional(),
  SHOW_ADS: z.boolean(),
  SHOW_MOZIO_GROUP_HOMEPAGE: z.boolean(),
  SHOW_PACE_LOADER: z.boolean(),
  ALLOW_ONLY_REGISTERED: z.boolean(),
  DENY_GUEST_USERS: z.boolean().optional(),
  ESIM_COVER_IMAGE: z.string(),
  ESIM_SUPPORT_EMAIL: z.string().min(1),
  ESIM_SUPPORT_PHONE_NUMBERS: z.array(z.tuple([z.string(), z.string().min(1)])).min(1).refine(
    (numbers) => {
      const defaultNumber = numbers.find((n) => n[0] === "default");
      if (!defaultNumber || defaultNumber[1].length === 0) {
        return false;
      }
      return true;
    },
    (value) => ({
      message: `ESIM_SUPPORT_PHONE_NUMBERS must have a default phone number but received: ${value}`
    })
  ),
  PARKING_COVER_IMAGE: z.string(),
  PARKING_SHOW_HEADER_ESIM: z.boolean(),
  PARKING_SHOW_HEADER_TRANSFER: z.boolean(),
  PARKING_SHOW_ESIM_BANNER: z.boolean(),
  PARKING_SHOW_TRANSFER_BANNER: z.boolean(),
  PARKING_SHOW_LOUNGES_BANNER: z.boolean(),
  SHOW_SIGNIN_BUTTON: z.boolean(),
  CUSTOM_LOGO_LINK: z.string().optional().nullable(),
  SHOW_RESEND_BOOKING: z.boolean(),
  REGISTER_TA_SHOW: z.boolean(),
  TRAVEL_AGENT_COMPANY: z.string().optional(),
  HEADER_CUSTOM_LINKS: z.array(z.tuple([z.string(), z.string()])),
  SHOW_TA_REPORT_BUTTON: z.boolean(),
  EDITABLE_MY_INFORMATION: z.boolean(),
  EMAIL_CHANGE_ABILITY: z.boolean(),
  TUI_CS_ADJUSTMENTS: z.boolean(),
  PASSWORD_RESET_ABILITY: z.boolean(),
  AGENT_IATA_AND_ID_ENABLED: z.boolean(),
  EDITABLE_TA_WITHDRAWAL: z.boolean(),
  EDITABLE_BOOKING_FEE: z.boolean(),
  EDITABLE_CUSTOMER_PROFILES: z.boolean(),
  EDITABLE_CUSTOMER_NOTIFICATIONS: z.boolean(),
  SHOW_MY_ACCOUNT: z.boolean(),
  SHOW_MY_BOOKINGS: z.boolean(),
  HEADER_USER_NAME: z.enum(["email", "name"]),
  HEADER_SHOW_TA_SUBTEXT: z.boolean(),
  ZENDESK_ENABLED: z.boolean(),
  HELP_CHAT_THEME_COLOR: z.enum([
    "FIRST_BACKGROUND_COLOR",
    "SECOND_BACKGROUND_COLOR",
    "THIRD_BACKGROUND_COLOR",
    "FOURTH_BACKGROUND_COLOR"
  ]),
  HELP_BUTTON_COLOR: z.enum([
    "FIRST_BACKGROUND_COLOR",
    "SECOND_BACKGROUND_COLOR",
    "THIRD_BACKGROUND_COLOR",
    "FOURTH_BACKGROUND_COLOR"
  ]),
  SHOW_YOU_EARNED: z.boolean(),
  NO_COMPACT_PRICE: z.boolean(),
  REQUIRED_CRUISE_SHIP_NAME: z.boolean().optional(),
  COVERAGE_MAP_SHOW: z.boolean(),
  SHOW_TESTIMONIALS_IN_HP: z.boolean(),
  HOME_FEATURES_ORDERED_LIST: z.array(z.tuple([z.string(), z.string()])).transform(
    (items) => items.filter(
      ([key]) => [
        "professional-drivers",
        "free-cancellation",
        "customer-support",
        "delay-wait",
        "instant-booking",
        "meet-n-greet",
        "vehicles-for-every-need",
        "miles",
        "budget-to-premium",
        ""
      ].includes(key)
    )
  ).optional(),
  SEARCH_FORM_COVER_IMAGE: z.string().optional(),
  COVER_OVERLAY_COLOR: z.string().optional(),
  SEARCHFORM_BACKGROUND_COLOR: z.string().optional(),
  SEARCH_COVER_POSITION: z.enum(["top", "center", "bottom"]).optional(),
  HOME_HEADER_COLOR: z.enum(["#FFFFFF", "#000000"]).optional(),
  SHOW_HEADER: z.boolean(),
  SHOW_FOOTER: z.boolean(),
  ESIM_ENABLE: z.boolean(),
  SHOW_PARKING_SEARCH: z.boolean(),
  CAR_RENTAL: z.boolean(),
  HEADER_CUSTOM_CONTENT: z.string().optional(),
  BODY_CUSTOM_CONTENT: z.string().optional(),
  TAG_MANAGER_KEY: z.string().optional(),
  TAG_MANAGER_URL_PREFIX: z.string().optional(),
  TAG_MANAGER_PATHNAME_MAP: z.array(z.tuple([z.string(), z.string()])).optional()
});
const whiteLabelSchema = z.object({
  config: configSchema
});
const parseBootstrapJs = (script, domain) => {
  if (script.includes("window.WL_OBJ = null")) {
    if (domain !== "www.mozio.com" && domain !== "front-end-staging.mozio.com") {
      console.error(`Api returns null when fetching ${domain}.`);
    }
    return;
  }
  if (script.startsWith("<html>")) {
    const regexResult = /<h1>(.*)<\/h1>/.exec(script) ?? [];
    const errorMessage = regexResult[1];
    console.error(`Api error when fetching ${domain}: ${errorMessage}`);
    return;
  }
  const whiteLabelData = whiteLabelSchema.parse(
    JSON.parse(script.replace("window.WL_OBJ = ", ""))
  );
  whiteLabelData.defaultDomain = domain;
  return whiteLabelData;
};
const getWhiteLabel = f$f.fetcher(
  async ({ ctx }, domain) => {
    const { whiteLabelUrl } = urlConfig$f[ctx.env];
    const cacheBucket = Math.floor(Date.now() / (2 * 60 * 1e3));
    const response = await fetch(
      `${whiteLabelUrl}/whitelabel/bootstrap.js?name=${domain}&_cb=${cacheBucket}`
    );
    const script = await response.text();
    return parseBootstrapJs(script, domain);
  }
);
const WHITE_LABELS = {
  esim: [
    "esim-test.mozio.com",
    // NOTE: for testing only
    "esim-agent-test.mozio.com",
    // NOTE: for testing only
    "esim-test-staging.mozio.com",
    // NOTE: for staging testing only
    // "tui-uk.mozio.com",
    // "tui-nl.mozio.com",
    // "tui-be.mozio.com",
    // "tui-de.mozio.com",
    // "tui-nordic.mozio.com",
    "itaairways.mozio.com",
    "stafftravelvoyage.mozio.com",
    "firstchoice.mozio.com",
    "downtowntravel.mozio.com",
    "wycieczkionline.mozio.com",
    "android.mozio.com",
    "ios.mozio.com",
    "adr.mozio.com"
  ],
  parking: [
    "esky.mozio.com",
    "stafftravelvoyage.mozio.com",
    "airportshuttles.mozio.com",
    "priceline.mozio.com",
    "statusprime.mozio.com",
    "luckytrip.mozio.com",
    "jal.mozio.com",
    "adamvacations.mozio.com",
    "android.mozio.com",
    "ios.mozio.com",
    "vacationport.mozio.com"
  ],
  userCenter: ["esky.mozio.com"]
};
const WHITE_LABELS_CUSTOM_DOMAINS = /* @__PURE__ */ new Set([
  "esim-test.mozio.com",
  // NOTE: for testing only
  "esim-agent-test.mozio.com",
  // NOTE: for testing only
  "esim-test-staging.mozio.com",
  // NOTE: for staging testing only
  "tui-uk.mozio.com",
  "tui-nl.mozio.com",
  "tui-be.mozio.com",
  "tui-de.mozio.com",
  "tui-no.mozio.com",
  "tui-fr.mozio.com",
  "tui-dk.mozio.com",
  "tui-fi.mozio.com",
  "tui-se.mozio.com",
  "tui-at.mozio.com",
  "itaairways.mozio.com"
]);
const getWhiteLabelDomains = (data) => {
  const customDomains = data.config.CUSTOM_DOMAINS.filter(
    (customDomain) => WHITE_LABELS_CUSTOM_DOMAINS.has(customDomain[0])
  ).map(
    ([domain, localeAndCurrency]) => ({
      ...data,
      domain,
      locale: localeAndCurrency.split(",")[0],
      currency: localeAndCurrency.split(",")[1]
    })
  );
  const customDefaultDomain = data.config.CUSTOM_DOMAINS.find(
    ([domain]) => domain === data.defaultDomain
  );
  if (!customDefaultDomain) {
    customDomains.push({
      ...data,
      domain: data.defaultDomain
    });
  }
  return customDomains;
};
const getWhiteLabelCustomDomains = (data) => {
  const customDomains = data.config.CUSTOM_DOMAINS.map(
    ([domain, localeAndCurrency]) => ({
      ...data,
      domain,
      locale: localeAndCurrency.split(",")[0],
      currency: localeAndCurrency.split(",")[1]
    })
  );
  const customDefaultDomain = data.config.CUSTOM_DOMAINS.find(
    ([domain]) => domain === data.defaultDomain
  );
  if (!customDefaultDomain) {
    customDomains.push({
      ...data,
      domain: data.defaultDomain
    });
  }
  return customDomains;
};
const getAllWhiteLabelsDomains = f$f.fetcher(
  async ({ ctx }, { projectName }) => {
    const { whiteLabelUrl } = urlConfig$f[ctx.env];
    const whiteLabels = await Promise.all(
      WHITE_LABELS[projectName].map(
        async (domain) => fetch(`${whiteLabelUrl}/whitelabel/bootstrap.js?name=${domain}`).then((response) => response.text()).then(
          (script) => parseBootstrapJs(script, domain)
        )
      )
    );
    return whiteLabels.filter(Boolean).flatMap(
      (whiteLabel) => getWhiteLabelDomains(whiteLabel)
    );
  }
);
const getBootstrapJsUrl = (env, domain) => `${urlConfig$f[env].whiteLabelUrl}/whitelabel/bootstrap.js?name=${domain}`;
const getAllWhiteLabelCustomDomains = (data) => {
  const customDomains = data.config.CUSTOM_DOMAINS.map(
    ([domain, localeAndCurrency]) => ({
      ...data,
      domain,
      locale: localeAndCurrency.split(",")[0],
      currency: localeAndCurrency.split(",")[1]
    })
  );
  const customDefaultDomain = data.config.CUSTOM_DOMAINS.find(
    ([domain]) => domain === data.defaultDomain
  );
  if (!customDefaultDomain) {
    customDomains.push({
      ...data,
      domain: data.defaultDomain
    });
  }
  return customDomains;
};
const getAllWhiteLabels = f$f.fetcher(
  async ({ ctx, get }) => {
    const { whiteLabelUrl } = urlConfig$f[ctx.env];
    const { data: whiteLabels } = await get(
      `${whiteLabelUrl}/whitelabel/configuration`,
      z.array(configSchema)
    );
    const whiteLabelsFormatted = whiteLabels.map(
      (whiteLabel) => ({
        config: {
          ...whiteLabel
        },
        defaultDomain: `${whiteLabel.NAME}.mozio.com`
      })
    );
    return whiteLabelsFormatted.filter(Boolean).flatMap(
      (whiteLabel) => getAllWhiteLabelCustomDomains(whiteLabel)
    );
  }
);
const createWhiteLabelQuery = f$f.combineFetchers({
  getWhiteLabel,
  getAllWhiteLabelsDomains,
  getAllWhiteLabels
});
const whiteLabelServices = {
  getInjectContent,
  getWhiteLabelDomains,
  getWhiteLabelCustomDomains,
  getBootstrapJsUrl
};

const urlConfig$e = {
  staging: {
    currencyUrl: "https://api-staging-internal.mozio.com/v2/currencies",
    geolocationUrl: "https://api-staging-internal.mozio.com/v2/geolocation"
  },
  production: {
    currencyUrl: "https://api.mozio.com/v2/currencies",
    geolocationUrl: "https://api.mozio.com/v2/geolocation"
  },
  testing: {
    currencyUrl: "https://api-testing.mozio.com/v2/currencies",
    geolocationUrl: "https://api-testing.mozio.com/v2/geolocation"
  }
};
const f$e = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    lang: z.string(),
    "api-key": z.string()
  }
});
const getCurrencies = f$e.fetcher(async ({ ctx, get }) => {
  const { currencyUrl } = urlConfig$e[ctx.env];
  const { data } = await get(
    `${currencyUrl}/`,
    z.object({
      currencies: z.array(
        z.object({
          code: z.string().min(1),
          name: z.string().min(1),
          symbol: z.string().min(1)
        })
      )
    })
  );
  return data.currencies;
});
const getCurrencyCodeByIp = f$e.fetcher(async ({ ctx, get }) => {
  const { geolocationUrl } = urlConfig$e[ctx.env];
  const { data } = await get(
    `${geolocationUrl}/`,
    z.object({
      currency_code: z.string().min(1),
      is_currency_supported: z.boolean()
    })
  );
  return data.is_currency_supported ? data.currency_code : "";
});
const createCurrencyQuery = f$e.combineFetchers({
  getCurrencies,
  getCurrencyCodeByIp
});

const urlConfig$d = {
  staging: {
    resendEmailUrl: "https://api-staging-internal.mozio.com/v2/resend-confirmation-email"
  },
  production: {
    resendEmailUrl: "https://api.mozio.com/v2/resend-confirmation-email"
  },
  testing: {
    resendEmailUrl: "https://api-testing.mozio.com/v2/resend-confirmation-email"
  }
};
const f$d = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    "api-key": z.string(),
    "Content-Type": z.string(),
    Lang: z.string()
  }
});
const resendConfirmationEmail$1 = f$d.fetcher(
  async ({ ctx, headers }, email) => {
    const { resendEmailUrl } = urlConfig$d[ctx.env];
    const response = await fetch(`${resendEmailUrl}/`, {
      method: "POST",
      headers,
      body: JSON.stringify({ email })
    });
    if (response.status !== 204) {
      const text = await response.text();
      let responseBody;
      if (text) {
        responseBody = JSON.parse(text);
      }
      throw new Error(
        responseBody?.message || "Failed to resend confirmation email"
      );
    }
    return true;
  }
);
const createConfirmationQuery = f$d.combineFetchers({
  resendConfirmationEmail: resendConfirmationEmail$1
});

const urlConfig$c = {
  staging: {
    reservationUrl: "https://api-staging-internal.mozio.com/v2/reservations",
    reportsUrl: "https://api-staging-internal.mozio.com/v2/travel_agent/reports"
  },
  production: {
    reservationUrl: "https://api.mozio.com/v2/reservations",
    reportsUrl: "https://api.mozio.com/v2/travel_agent/reports"
  },
  testing: {
    reservationUrl: "https://api-testing.mozio.com/v2/reservations",
    reportsUrl: "https://api-testing.mozio.com/v2/travel_agent/reports"
  }
};
const f$c = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    "api-key": z.string(),
    "Content-Type": z.string(),
    Authorization: z.string().optional(),
    Lang: z.string()
  }
});
const reservationSchema$1 = z.object({
  id: z.string().min(1),
  confirmation_number: z.string().min(1),
  user_first_name: z.string().optional(),
  user_last_name: z.string().optional(),
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  total_price: z.object({
    compact: z.string().min(1),
    display: z.string().min(1)
  }),
  voyage: z.object({
    departure_datetime: z.string().min(1),
    start_location: z.object({
      full_address: z.string().optional(),
      formatted_address: z.string().optional(),
      custom_name: z.string().optional()
    }),
    end_location: z.object({
      city: z.string().optional(),
      full_address: z.string().optional(),
      formatted_address: z.string().optional(),
      custom_name: z.string().optional()
    })
  }),
  timestamp: z.string().min(1)
});
const reservationsSchema = z.union([
  z.object({
    count: z.number(),
    next: z.string().nullable(),
    previous: z.string().nullable(),
    results: z.array(reservationSchema$1)
  }),
  authErrorSchema
]);
const getReservations$2 = f$c.fetcher(
  async ({ ctx, get }, {
    ordering,
    past,
    page,
    pageSize,
    includeCancellations,
    filters
  }) => {
    const { reservationUrl } = urlConfig$c[ctx.env];
    const {
      customerFirstName,
      customerLastName,
      customerEmail,
      agentEmail,
      reservationId,
      bookingDate,
      rideDate
    } = filters || {};
    const params = {
      ...ordering ? { ordering } : {},
      ...past ? { past: "true" } : { past: "false" },
      ...page ? { page: page.toString() } : {},
      ...pageSize ? { page_size: pageSize.toString() } : {},
      ...includeCancellations ? { include_cancellations: "true" } : {},
      ...customerFirstName ? { customer_first_name: customerFirstName.value } : {},
      ...customerLastName ? { customer_last_name: customerLastName.value } : {},
      ...customerEmail ? { customer_email: customerEmail.value } : {},
      ...agentEmail ? { extra_email: agentEmail.value } : {},
      ...reservationId ? { confirmation_number: reservationId.value } : {},
      ...bookingDate ? {
        timestamp__from: bookingDate.value.startDate.toISOString(),
        timestamp__to: bookingDate.value.endDate.toISOString()
      } : {},
      ...rideDate ? {
        departure__from: rideDate.value.startDate.toISOString(),
        departure__to: rideDate.value.endDate.toISOString()
      } : {}
    };
    const paramsString = new URLSearchParams(params).toString();
    const { data, response } = await get(
      `${reservationUrl}/?${paramsString}`,
      reservationsSchema
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if ("code" in data) {
      throw new Error(data.message ?? "");
    }
    return {
      count: data.count,
      next: data.next,
      previous: data.previous,
      results: data.results.map(
        (result) => ({
          id: result.id,
          confirmationNumber: result.confirmation_number,
          firstName: result.first_name,
          lastName: result.last_name,
          initiatedBy: result.user_first_name ? `${result.user_first_name ?? ""} ${result.user_last_name ?? ""}` : void 0,
          totalPrice: {
            compact: result.total_price.compact,
            display: result.total_price.display
          },
          voyage: {
            departureDatetime: result.voyage.departure_datetime,
            startLocation: {
              fullAddress: result.voyage.start_location.full_address,
              formattedAddress: result.voyage.start_location.formatted_address,
              customName: result.voyage.start_location.custom_name
            },
            endLocation: {
              fullAddress: result.voyage.end_location.full_address,
              formattedAddress: result.voyage.end_location.formatted_address,
              customName: result.voyage.end_location.custom_name
            }
          },
          timestamp: result.timestamp
        })
      )
    };
  }
);
const travelAgentReservationSchema = reservationSchema$1.extend({
  total_price: z.string().min(1)
}).omit({ total_price: true }).merge(
  z.object({
    total_price: z.string().min(1),
    reservation_fee: z.string().min(1)
  })
);
const travelAgentReservationsSchema = z.object({
  reservations_total: z.string(),
  commissions_total: z.string(),
  withdrawn: z.boolean(),
  reservations: z.array(travelAgentReservationSchema)
});
const getTravelAgentReservations = f$c.fetcher(
  async ({ ctx, get }, { month, year }) => {
    const { reservationUrl } = urlConfig$c[ctx.env];
    const { data, response } = await get(
      `${reservationUrl}/travel_agent_reservations/${month}/${year}/`,
      travelAgentReservationsSchema
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    return {
      reservationsTotal: data.reservations_total,
      commissionsTotal: data.commissions_total,
      withdrawn: data.withdrawn,
      reservations: data.reservations.map(
        (result) => ({
          id: result.id,
          confirmationNumber: result.confirmation_number,
          firstName: result.first_name,
          lastName: result.last_name,
          totalPrice: result.total_price,
          voyage: {
            departureDatetime: result.voyage.departure_datetime,
            startLocation: {
              fullAddress: result.voyage.start_location.full_address,
              formattedAddress: result.voyage.start_location.formatted_address,
              customName: result.voyage.start_location.custom_name
            },
            endLocation: {
              fullAddress: result.voyage.end_location.full_address,
              formattedAddress: result.voyage.end_location.formatted_address,
              customName: result.voyage.end_location.custom_name
            }
          },
          timestamp: result.timestamp,
          reservationFee: result.reservation_fee
        })
      )
    };
  }
);
const reportSchema = z.object({
  url: z.string()
});
const getReportUrl = f$c.fetcher(
  async ({ ctx, get }, {
    startMonth,
    startYear,
    endMonth,
    endYear,
    travelDateInterval,
    includeWithdrawn
  }) => {
    const { reportsUrl } = urlConfig$c[ctx.env];
    const params = {
      travel_date: travelDateInterval ? "1" : "0",
      include_withdrawn: includeWithdrawn.toString()
    };
    const paramsString = new URLSearchParams(params).toString();
    const { data, response } = await get(
      `${reportsUrl}/${startMonth}/${startYear}/${endMonth}/${endYear}/?${paramsString}`,
      reportSchema
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    return {
      url: data.url
    };
  }
);
const getReservationByIdResponseSchema = z.union([
  reservationSchema$1,
  authErrorSchema
]);
const getReservationById = f$c.fetcher(
  async ({ ctx, get }, { reservationId }) => {
    const { reservationUrl } = urlConfig$c[ctx.env];
    const { data, response } = await get(
      `${reservationUrl}/${reservationId}/`,
      getReservationByIdResponseSchema
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if ("code" in data) {
      throw new Error(data.message ?? "");
    }
    return {
      id: data.id,
      confirmationNumber: data.confirmation_number,
      firstName: data.first_name,
      lastName: data.last_name,
      initiatedBy: data.user_first_name ? `${data.user_first_name ?? ""} ${data.user_last_name ?? ""}` : void 0,
      totalPrice: {
        compact: data.total_price.compact,
        display: data.total_price.display
      },
      voyage: {
        departureDatetime: data.voyage.departure_datetime,
        startLocation: {
          fullAddress: data.voyage.start_location.full_address,
          formattedAddress: data.voyage.start_location.formatted_address,
          customName: data.voyage.start_location.custom_name
        },
        endLocation: {
          city: data.voyage.end_location.city,
          fullAddress: data.voyage.end_location.full_address,
          formattedAddress: data.voyage.end_location.formatted_address,
          customName: data.voyage.end_location.custom_name
        }
      },
      timestamp: data.timestamp
    };
  }
);
const createTransferQuery = f$c.combineFetchers({
  getReservations: getReservations$2,
  getReportUrl,
  getTravelAgentReservations,
  getReservationById
});

const urlConfig$b = {
  staging: {
    userUrl: "https://api-staging-internal.mozio.com/v2/auth/users"
  },
  production: {
    userUrl: "https://api.mozio.com/v2/auth/users"
  },
  testing: {
    userUrl: "https://api-testing.mozio.com/v2/auth/users"
  }
};
const f$b = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    "api-key": z.string(),
    "Content-Type": z.string(),
    Authorization: z.string().optional(),
    Lang: z.string()
  }
});
const USER_TYPE = {
  NORMAL: 0,
  TRAVEL_AGENT: 1,
  CORPORATE: 2,
  GBT_TC: 3
};
const fieldErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  user_message: z.string().nullable()
});
const errorSchema$2 = z.record(z.array(fieldErrorSchema));
const userSchema = z.object({
  id: z.number(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  user_type: z.nativeEnum(USER_TYPE),
  country_code_name: z.string().nullable(),
  country_code: z.string(),
  phone_number_national: z.string().optional(),
  currency: z.string(),
  language: z.string()
});
const responseSchema = z.union([userSchema, authErrorSchema, errorSchema$2]);
const isUserData = (data) => {
  return data.email !== void 0;
};
const getMyUser = f$b.fetcher(async ({ ctx, get }) => {
  const { userUrl } = urlConfig$b[ctx.env];
  const { data, response } = await get(
    `${userUrl}/me`,
    z.union([userSchema, authErrorSchema])
  );
  if (response.status === 403) {
    throw new AuthError();
  }
  if ("code" in data) {
    throw new Error(data.message ?? "");
  }
  return {
    email: data.email,
    firstName: data.first_name,
    lastName: data.last_name,
    userType: data.user_type,
    countryCode: data.country_code_name ?? void 0,
    callingCode: data.country_code,
    phoneNumber: data.phone_number_national,
    currency: data.currency,
    locale: data.language,
    id: data.id
  };
});
const updateMyUser = f$b.fetcher(
  async ({ ctx, patch }, {
    id,
    firstName,
    lastName,
    email,
    countryCode,
    phoneNumber,
    currency,
    locale
  }) => {
    const { userUrl } = urlConfig$b[ctx.env];
    const { data, response } = await patch(
      `${userUrl}/${id}/`,
      responseSchema,
      {
        first_name: firstName,
        last_name: lastName,
        email,
        country_code_name: countryCode.toLowerCase(),
        phone_number: phoneNumber,
        currency,
        language: locale
      }
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if (response.status === 400) {
      const errors = [];
      Object.values(data).forEach((errorArray) => {
        errorArray.forEach((error) => {
          errors.push({
            message: error.message,
            code: error.code,
            userMessage: error.user_message || ""
          });
        });
      });
      return {
        errors
      };
    }
    if (response.ok && isUserData(data)) {
      return {
        email: data.email,
        firstName: data.first_name,
        lastName: data.last_name,
        userType: data.user_type,
        countryCode: data.country_code_name ?? void 0,
        callingCode: data.country_code,
        phoneNumber: data.phone_number_national,
        currency: data.currency,
        locale: data.language,
        id: data.id
      };
    }
    throw new Error("Something went wrong");
  }
);
const changePassword = f$b.fetcher(
  async ({ ctx, post }, { id, oldPassword, newPassword }) => {
    const { userUrl } = urlConfig$b[ctx.env];
    const { data, response } = await post(
      `${userUrl}/${id}/change_password/`,
      z.union([
        z.object({
          success: z.boolean(),
          msg: z.string().optional()
        }),
        authErrorSchema
      ]),
      {
        old_password: oldPassword,
        password: newPassword
      }
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if ("code" in data) {
      throw new Error(data.message || "");
    }
    if (!data.success) {
      throw new Error(data.msg);
    }
    return {
      success: data.success
    };
  }
);
const createUsersQuery = f$b.combineFetchers({
  getMyUser,
  updateMyUser,
  changePassword
});

const urlConfig$a = {
  staging: {
    locationUrl: "https://api-staging-internal.mozio.com/v2/locations",
    geolocationUrl: "https://api-staging-internal.mozio.com/v2/geolocation"
  },
  production: {
    locationUrl: "https://api.mozio.com/v2/locations",
    geolocationUrl: "https://api.mozio.com/v2/geolocation"
  },
  testing: {
    locationUrl: "https://api-testing.mozio.com/v2/locations",
    geolocationUrl: "https://api-testing.mozio.com/v2/geolocation"
  }
};
const f$a = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    lang: z.string(),
    "api-key": z.string()
  }
});
const locationSchema$3 = z.object({
  display: z.string().min(1),
  name: z.string().min(1).nullable(),
  value: z.string().min(1),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  type: z.string().optional(),
  place_id: z.string().nullable().optional()
});
const airportLocationSchema = z.object({
  display: z.string().min(1),
  name: z.string().min(1),
  value: z.string().min(1),
  lat: z.number(),
  lng: z.number()
});
const queryAirports = f$a.fetcher(
  async ({ ctx, get }, { query }) => {
    if (query.length < 3) {
      return [];
    }
    const { locationUrl } = urlConfig$a[ctx.env];
    const { data } = await get(
      `${locationUrl}/airports/?query=${query}`,
      z.object({
        results: z.array(airportLocationSchema.passthrough())
      })
    );
    return data.results.map(
      (location) => ({
        displayText: location.display,
        name: location.name,
        value: location.value,
        lat: location.lat,
        lng: location.lng
      })
    );
  }
);
const queryLocations = f$a.fetcher(
  async ({ ctx, get }, {
    query,
    includeTrainStations = true,
    includePorts = true,
    includeFar = false,
    type,
    value,
    display,
    name,
    main_text,
    secondary_text,
    place_id,
    lat,
    lng,
    strictbounds
  }) => {
    const { locationUrl } = urlConfig$a[ctx.env];
    const params = new URLSearchParams({
      query,
      include_train_stations: String(includeTrainStations),
      include_ports: String(includePorts),
      include_far: String(includeFar)
    });
    const optionalParams = {
      type,
      value,
      display,
      name,
      main_text,
      secondary_text,
      place_id,
      lat,
      lng,
      strictbounds
    };
    for (const [key, val] of Object.entries(optionalParams)) {
      if (val !== void 0) {
        params.set(key, String(val));
      }
    }
    const queryString = `?${params.toString()}`;
    const { data } = await get(
      `${locationUrl}/query/${queryString}`,
      z.object({
        results: z.array(locationSchema$3.passthrough())
      })
    );
    return data.results.map(
      (location) => ({
        displayText: location.display,
        name: location.name ?? location.display,
        value: location.value,
        lat: location.lat,
        lng: location.lng,
        type: location.type,
        id: location.place_id
      })
    );
  }
);
const geocodeLocation = f$a.fetcher(
  async ({ ctx, get }, { lat, lng }) => {
    const { locationUrl } = urlConfig$a[ctx.env];
    const geocodeResultSchema = z.object({
      geometry: z.object({
        location: z.object({
          lat: z.number(),
          lng: z.number()
        })
      }),
      place_id: z.string(),
      formatted_address: z.string().optional(),
      short_name: z.string().optional(),
      types: z.array(z.string()).optional(),
      address_components: z.array(
        z.object({
          long_name: z.string(),
          short_name: z.string(),
          types: z.array(z.string())
        })
      ).optional()
    });
    const { data } = await get(
      `${locationUrl}/geocode/?lat=${lat}&lng=${lng}`,
      z.object({ results: z.array(geocodeResultSchema) })
    );
    return data.results.map((r) => {
      const displayText = r.short_name ?? r.formatted_address ?? r.place_id;
      const componentTypes = r.address_components?.flatMap((c) => c.types) ?? [];
      const type = componentTypes.includes("airport") ? "airport" : componentTypes.includes("train_station") || componentTypes.includes("transit_station") ? "station" : void 0;
      return {
        displayText,
        name: displayText,
        value: r.place_id,
        lat: r.geometry.location.lat,
        lng: r.geometry.location.lng,
        ...type && { type }
      };
    });
  }
);
const getLocationDetails = f$a.fetcher(
  async ({ ctx, get }, { placeId }) => {
    const { locationUrl } = urlConfig$a[ctx.env];
    const detailsSchema = z.object({
      result: z.object({
        place_id: z.string(),
        short_name: z.string().optional(),
        formatted_address: z.string().optional(),
        geometry: z.object({
          location: z.object({
            lat: z.number(),
            lng: z.number()
          })
        }),
        address_components: z.array(
          z.object({
            long_name: z.string(),
            short_name: z.string(),
            types: z.array(z.string())
          })
        ).optional()
      })
    });
    const { data } = await get(
      `${locationUrl}/details/?place_id=${encodeURIComponent(placeId)}`,
      detailsSchema
    );
    const r = data.result;
    const displayText = r.short_name ?? r.formatted_address ?? r.place_id;
    const types = r.address_components?.flatMap((c) => c.types) ?? [];
    const type = types.includes("airport") ? "airport" : types.includes("train_station") || types.includes("transit_station") ? "station" : void 0;
    return [
      {
        displayText,
        name: displayText,
        value: r.place_id,
        lat: r.geometry.location.lat,
        lng: r.geometry.location.lng,
        ...type && { type }
      }
    ];
  }
);
const GetUserCountryResponseSchema = z.object({
  country_code: z.string(),
  country_name: z.string()
});
const getUserCountryByIp = f$a.fetcher(
  async ({
    ctx,
    get
  }) => {
    const { geolocationUrl } = urlConfig$a[ctx.env];
    const { data } = await get(
      `${geolocationUrl}/`,
      GetUserCountryResponseSchema
    );
    return data;
  }
);
const createLocationQuery = f$a.combineFetchers({
  queryAirports,
  queryLocations,
  getLocationDetails,
  geocodeLocation,
  getUserCountryByIp
});

const urlConfig$9 = {
  staging: {
    parkingUrl: "https://parking-staging.mozio.com/v1"
  },
  production: {
    parkingUrl: "https://parking-api.mozio.com/v1"
  },
  testing: {
    parkingUrl: "https://parking-api-testing.mozio.com/v1"
  }
};
const f$9 = new FetcherClient({
  ctx: z.object({
    env: envSchema,
    currency: z.string()
  }),
  headers: {
    lang: z.string(),
    Authorization: z.string(),
    "white-label-id": z.number().optional(),
    "white-label-name": z.string(),
    "partner-ref": z.string(),
    hostname: z.string(),
    "distinct-id": z.union([z.string(), z.number()]).optional()
  }
});
const errorSchema$1 = z.object({
  code: z.enum([
    "search_expired",
    "reservation_not_exist",
    "payment_error",
    "paymentintent_not_chargeable",
    "payment_information_missing",
    "invalid_coupon",
    "changes_not_allowed_cannot_cancel"
  ]),
  message: z.string()
});
const ERROR_CODES = {
  SEARCH_EXPIRED: "search_expired",
  RESERVATION_NOT_EXIST: "reservation_not_exist",
  PAYMENT_ERROR: "payment_error",
  PAYMENT_INTENT_NOT_CHARGEABLE: "paymentintent_not_chargeable",
  PAYMENT_INFORMATION_MISSING: "payment_information_missing",
  INVALID_COUPON: "invalid_coupon",
  CHANGES_NOT_ALLOWED_CANNOT_CANCEL: "changes_not_allowed_cannot_cancel"
};
const isExpectedError = (code) => Object.values(ERROR_CODES).includes(code);
const locationSchema$2 = z.object({
  value: z.string(),
  name: z.string(),
  lat: z.number().nullable(),
  lng: z.number().nullable()
});
const PARKING_TRANSFER_TYPE = {
  WALK: "WALK",
  SHUTTLE: "SHUTTLE",
  MEET_AND_GREET: "MEET_AND_GREET"
};
const PARKING_FEATURE_TYPE = {
  FREE_CANCELLATION: "FREE_CANCELLATION",
  COVERED: "COVERED",
  UNCOVERED: "UNCOVERED",
  ACCESSIBLE: "ACCESSIBLE",
  OPEN_24_7: "OPEN_24_7",
  CCTV: "CCTV",
  KEY_DROP_OFF: "KEY_DROP_OFF"
};
const searchResponseSchema = z.object({
  search_id: z.string(),
  more_coming: z.boolean(),
  arrival_datetime_display: z.preprocess(
    (date) => new Date(z.string().parse(date)),
    z.date()
  ),
  departure_datetime_display: z.preprocess(
    (date) => new Date(z.string().parse(date)),
    z.date()
  ),
  location: locationSchema$2,
  search_context_hash: z.string(),
  expires_at: z.number(),
  results: z.array(
    z.object({
      result_id: z.string(),
      provider: z.object({
        id: z.string(),
        name: z.string()
      }),
      location: locationSchema$2,
      name: z.string(),
      description: z.string(),
      price: z.object({
        amount: z.string(),
        currency_code: z.string()
      }),
      image_url: z.string().url().nullable().optional(),
      logo_url: z.string().url().nullable().optional(),
      detailed_instructions_html: z.string().nullable(),
      basic_instructions_html: z.string(),
      cancellation: z.object({
        notice_in_hours: z.number(),
        refund_percentage: z.number()
      }).optional(),
      // TODO: could be undefined?
      airport_transfer_summary: z.object({
        id: z.string(),
        name: z.string(),
        distance_meter: z.number().nullable(),
        type: z.enum(["WALK", "SHUTTLE", "MEET_AND_GREET"]),
        descriptions: z.array(z.string())
      }),
      features: z.array(
        z.object({
          id: z.string(),
          type: z.enum([
            "FREE_CANCELLATION",
            "COVERED",
            "UNCOVERED",
            "ACCESSIBLE",
            "OPEN_24_7",
            "CCTV",
            "KEY_DROP_OFF"
          ]),
          name: z.string(),
          description: z.string()
        })
      )
    })
  )
});
const mapLocation = (location) => ({
  value: location.value,
  name: location.name,
  lat: location.lat ?? void 0,
  lng: location.lng ?? void 0
});
const mapParkingSearchResult = (result) => {
  return {
    id: result.result_id,
    name: result.name,
    price: {
      amount: Number.parseFloat(result.price.amount),
      currency: result.price.currency_code
    },
    location: mapLocation(result.location),
    imageUrl: result.image_url ?? void 0,
    logoUrl: result.logo_url ?? void 0,
    providerId: result.provider.id,
    resultId: result.result_id,
    description: result.description,
    basicInstructionsHtml: result.basic_instructions_html,
    detailedInstructionsHtml: result.detailed_instructions_html ?? void 0,
    airportTransferSummary: {
      ...result.airport_transfer_summary,
      // NOTE: if we don't know the distance, we consider it infinite so it's sorted last
      distanceMeter: result.airport_transfer_summary.distance_meter ?? Number.POSITIVE_INFINITY
    },
    features: result.features
  };
};
const initiateSearch = f$9.fetcher(
  async ({ ctx, post }, {
    arrivalDateTime,
    departureDateTime,
    location,
    partnerRef
  }) => {
    const { parkingUrl } = urlConfig$9[ctx.env];
    const { data } = await post(`${parkingUrl}/search`, searchResponseSchema, {
      arrival_datetime: arrivalDateTime,
      departure_datetime: departureDateTime,
      location: locationSchema$2.parse(location),
      ref: partnerRef,
      currency: ctx.currency
    });
    return {
      searchId: data.search_id,
      location: mapLocation(data.location),
      arrivalDateTime: data.arrival_datetime_display,
      departureDateTime: data.departure_datetime_display,
      results: [],
      searchContextHash: data.search_context_hash,
      moreComing: data.more_coming,
      expiresAt: data.expires_at
    };
  }
);
const getSearchResults = f$9.fetcher(
  async ({ ctx, get }, { searchId }) => {
    const { parkingUrl } = urlConfig$9[ctx.env];
    const { data } = await get(
      `${parkingUrl}/search/${searchId}/poll`,
      searchResponseSchema
    );
    return {
      searchId: data.search_id,
      location: mapLocation(data.location),
      arrivalDateTime: data.arrival_datetime_display,
      departureDateTime: data.departure_datetime_display,
      results: data.results.map(
        (result) => mapParkingSearchResult(result)
      ),
      searchContextHash: data.search_context_hash,
      moreComing: data.more_coming,
      expiresAt: data.expires_at
    };
  }
);
const getSearchResult = f$9.fetcher(
  async ({ ctx, get }, { searchId, resultId, providerId }) => {
    const { parkingUrl } = urlConfig$9[ctx.env];
    const { data } = await get(
      `${parkingUrl}/search/${searchId}/${resultId}?provider_id=${providerId}&currency=${ctx.currency}`,
      z.union([errorSchema$1, searchResponseSchema])
    );
    if ("code" in data && isExpectedError(data.code)) {
      return {
        error: data.code,
        message: data.message
      };
    }
    if ("search_id" in data) {
      return {
        searchId: data.search_id,
        results: data.results.map(
          (result) => mapParkingSearchResult(result)
        ),
        arrivalDateTime: data.arrival_datetime_display,
        departureDateTime: data.departure_datetime_display,
        location: mapLocation(data.location),
        searchContextHash: data.search_context_hash,
        moreComing: data.more_coming,
        expiresAt: data.expires_at
      };
    }
    throw new Error(`An unexpected error has occurred. Error: ${data.code}`);
  }
);
const updateSearchResult = f$9.fetcher(
  async ({ ctx, patch }, {
    searchId,
    resultId,
    providerId,
    arrivalDateTime,
    departureDateTime,
    location
  }) => {
    const { parkingUrl } = urlConfig$9[ctx.env];
    const { data } = await patch(
      `${parkingUrl}/search/${searchId}/${resultId}?provider_id=${providerId}&currency=${ctx.currency}`,
      z.union([errorSchema$1, searchResponseSchema]),
      {
        arrival_datetime: arrivalDateTime,
        departure_datetime: departureDateTime,
        location
      }
    );
    if ("code" in data && isExpectedError(data.code)) {
      return {
        error: data.code,
        message: data.message
      };
    }
    if ("search_id" in data) {
      return {
        searchId: data.search_id,
        results: data.results.map(
          (result) => mapParkingSearchResult(result)
        ),
        arrivalDateTime: data.arrival_datetime_display,
        departureDateTime: data.departure_datetime_display,
        location: mapLocation(data.location),
        searchContextHash: data.search_context_hash,
        moreComing: data.more_coming,
        expiresAt: data.expires_at
      };
    }
    throw new Error(`An unexpected error has occurred. Error: ${data.code}`);
  }
);
const paymentIntentResultSchema = z.object({
  paymentintent_id: z.string(),
  client_secret: z.string()
});
const createOrUpdatePaymentIntent = async ({
  post,
  currency,
  amount,
  parkingUrl,
  resultId,
  searchId,
  paymentIntentId
}) => {
  const { data } = await post(
    `${parkingUrl}/payment-intent/${paymentIntentId}`,
    z.union([paymentIntentResultSchema, errorSchema$1]),
    {
      result_id: resultId,
      search_id: searchId,
      currency,
      amount
    }
  );
  if ("code" in data) {
    return {
      error: data.code,
      message: data.message
    };
  }
  return {
    paymentIntentId: data.paymentintent_id,
    clientSecret: data.client_secret
  };
};
const calculatePricingResultSchema = z.object({
  currency: z.string(),
  coupon_code: z.string(),
  coupon_discount: z.object({
    display: z.string(),
    value: z.string()
  }),
  original_price: z.object({
    display: z.string(),
    value: z.string()
  }),
  final_price: z.object({
    display: z.string(),
    value: z.string(),
    smallest_currency_unit: z.number()
  }),
  total_price: z.object({
    display: z.string(),
    value: z.string()
  }),
  platform_fee: z.object({
    display: z.string(),
    value: z.string()
  })
});
const mapPricingResult = (result) => ({
  currency: result.currency,
  couponCode: result.coupon_code,
  couponDiscount: {
    display: result.coupon_discount.display,
    value: result.coupon_discount.value
  },
  originalPrice: {
    display: result.original_price.display,
    value: result.original_price.value
  },
  finalPrice: {
    display: result.final_price.display,
    value: result.final_price.value,
    smallestCurrencyUnit: result.final_price.smallest_currency_unit
  },
  totalPrice: {
    display: result.total_price.display,
    value: result.total_price.value
  }
});
const calculatePricing = f$9.fetcher(
  async ({ ctx, post }, {
    searchId,
    resultId,
    providerId,
    couponCode,
    paymentIntentId
  }) => {
    const { parkingUrl } = urlConfig$9[ctx.env];
    const { data } = await post(
      `${parkingUrl}/pricing`,
      z.union([calculatePricingResultSchema, errorSchema$1]),
      {
        search_id: searchId,
        result_id: resultId,
        provider_id: providerId,
        coupon_code: couponCode,
        currency: ctx.currency
      }
    );
    let result = data;
    let invalidCoupon = false;
    if ("code" in result && isExpectedError(result.code)) {
      if (result.code !== ERROR_CODES.INVALID_COUPON) {
        return {
          error: result.code,
          message: result.message
        };
      }
      invalidCoupon = true;
      const { data: data2 } = await post(
        `${parkingUrl}/pricing`,
        z.union([calculatePricingResultSchema, errorSchema$1]),
        {
          search_id: searchId,
          result_id: resultId,
          provider_id: providerId
        }
      );
      result = data2;
      if ("code" in result && isExpectedError(result.code)) {
        return {
          error: result.code,
          message: result.message
        };
      }
    }
    if ("currency" in result) {
      let paymentIntentData;
      if (result.final_price.smallest_currency_unit > 0) {
        paymentIntentData = await createOrUpdatePaymentIntent({
          post,
          currency: result.currency,
          amount: Number.parseFloat(result.final_price.value),
          parkingUrl,
          resultId,
          searchId,
          paymentIntentId
        });
        if ("error" in paymentIntentData) {
          return {
            error: paymentIntentData.error,
            message: paymentIntentData.message
          };
        }
      }
      return {
        invalidCoupon,
        paymentIntentId: paymentIntentData && "paymentIntentId" in paymentIntentData ? paymentIntentData.paymentIntentId : void 0,
        clientSecret: paymentIntentData && "clientSecret" in paymentIntentData ? paymentIntentData.clientSecret : void 0,
        ...mapPricingResult(result)
      };
    }
    throw new Error(`An unexpected error has occurred. Error: ${result.code}`);
  }
);
const createReservationResponseSchema = z.object({
  status: z.enum(["pending", "paid", "confirmed", "cancelled", "completed"]).nullable(),
  reservations: z.array(
    z.object({
      id: z.string(),
      result_id: z.string(),
      search_id: z.string()
    })
  )
});
const PARKING_STATUS_TYPE = {
  PENDING: "pending",
  PAID: "paid",
  CONFIRMED: "confirmed",
  CANCELLED: "cancelled",
  COMPLETED: "completed"
};
const createReservation = f$9.fetcher(
  async ({ ctx, post }, {
    resultId,
    searchId,
    providerId,
    searchContextHash,
    vehicle,
    customer,
    passengerCount,
    travelReason,
    couponCode,
    paymentIntentId,
    departureFlight,
    arrivalFlight
  }) => {
    const { parkingUrl } = urlConfig$9[ctx.env];
    const { data } = await post(
      `${parkingUrl}/reservations`,
      z.union([createReservationResponseSchema, errorSchema$1]),
      {
        result_id: resultId,
        search_id: searchId,
        provider_id: providerId,
        search_context_hash: searchContextHash,
        vehicle: {
          ...vehicle,
          colour: vehicle.color,
          // cspell:disable-line
          registration_number: vehicle.registrationNumber
        },
        customer: {
          // TODO: Validate with BE what is this field
          title: "",
          first_name: customer.firstName,
          last_name: customer.lastName,
          email: customer.email,
          phone: customer.phone
        },
        voyages: [
          departureFlight.flightCode && {
            code: departureFlight.flightCode,
            terminal: departureFlight.originTerminal ?? "0",
            direction: "outbound"
          },
          arrivalFlight.flightCode && {
            code: arrivalFlight.flightCode,
            terminal: arrivalFlight.destinationTerminal ?? "0",
            direction: "inbound"
          }
        ].filter(Boolean),
        num_passengers: passengerCount ? Number.parseFloat(passengerCount) : void 0,
        travel_reason: travelReason,
        coupon_code: couponCode,
        paymentintent_id: paymentIntentId
      }
    );
    if ("code" in data && isExpectedError(data.code)) {
      return {
        error: data.code,
        message: data.message
      };
    }
    if ("status" in data) {
      return {
        id: data?.reservations[0]?.id
      };
    }
    throw new Error(`An unexpected error has occurred. Error: ${data.code}`);
  }
);
const getReservationResponseSchema = z.object({
  id: z.string(),
  customer: z.object({
    first_name: z.string(),
    email: z.string()
  }),
  carpark_name: z.string(),
  search_context: z.object({
    arrival_datetime: z.string(),
    departure_datetime: z.string(),
    location: z.object({
      name: z.string()
    })
  }),
  payment: z.object({
    currency: z.string(),
    total_price: z.string(),
    final_price: z.string(),
    discounts: z.object({
      coupon: z.object({
        discount_type: z.enum(["PERCENTAGE", "FIXED"]),
        percentage_amount: z.number(),
        amount: z.string()
      }).optional().nullable()
    }).optional().nullable(),
    coupon_code: z.string().optional().nullable()
  }),
  status: z.enum(["pending", "paid", "confirmed", "cancelled", "completed"]),
  cancellation: z.object({
    notice_in_hours: z.number(),
    refund_percentage: z.number()
  })
});
const getReservation$1 = f$9.fetcher(
  async ({ ctx, get }, { reservationId }) => {
    const { parkingUrl } = urlConfig$9[ctx.env];
    const { data } = await get(
      `${parkingUrl}/reservations/${reservationId}`,
      z.union([getReservationResponseSchema, errorSchema$1])
    );
    if ("code" in data && isExpectedError(data.code)) {
      return {
        error: data.code,
        message: data.message
      };
    }
    if ("id" in data) {
      return mapReservation(data);
    }
    throw new Error(`An unexpected error has occurred. Error: ${data.code}`);
  }
);
const getReservations$1 = f$9.fetcher(
  async ({ ctx, get }, { email }) => {
    const { parkingUrl } = urlConfig$9[ctx.env];
    const { data } = await get(
      `${parkingUrl}/reservations?email=${encodeURIComponent(email)}`,
      z.array(getReservationResponseSchema).or(errorSchema$1)
    );
    if ("code" in data && isExpectedError(data.code)) {
      return {
        error: data.code,
        message: data.message
      };
    }
    if (Array.isArray(data)) {
      return data.map(
        (reservation) => mapReservation(reservation)
      );
    }
    throw new Error(`An unexpected error has occurred. Error: ${data.code}`);
  }
);
const cancelReservation = f$9.fetcher(
  async ({ ctx, delete: _delete }, { reservationId, reason }) => {
    const { parkingUrl } = urlConfig$9[ctx.env];
    const { data } = await _delete(
      `${parkingUrl}/reservations/${reservationId}/`,
      z.union([getReservationResponseSchema, errorSchema$1]),
      {
        reason
      },
      {
        headers: {
          "Content-Type": "application/json;charset=utf-8"
        }
      }
    );
    if ("code" in data && isExpectedError(data.code)) {
      return {
        error: data.code,
        message: data.message
      };
    }
    if ("id" in data) {
      return {
        id: data.id,
        status: data.status
      };
    }
    throw new Error(`An unexpected error has occurred. Error: ${data.code}`);
  }
);
const resendNotificationResponseSchema = z.object({
  customer_email: z.string()
});
const resendNotification = f$9.fetcher(
  async ({ ctx, get }, { reservationId }) => {
    const { parkingUrl } = urlConfig$9[ctx.env];
    const { data } = await get(
      `${parkingUrl}/notifications/${reservationId}`,
      z.union([resendNotificationResponseSchema, errorSchema$1])
    );
    if ("code" in data && isExpectedError(data.code)) {
      return {
        error: data.code,
        message: data.message
      };
    }
    if ("customer_email" in data) {
      return {
        customerEmail: data.customer_email
      };
    }
    throw new Error(`An unexpected error has occurred. Error: ${data.code}`);
  }
);
const mapReservation = (reservation) => ({
  id: reservation.id,
  customer: {
    firstName: reservation.customer.first_name,
    email: reservation.customer.email
  },
  parkingName: reservation.carpark_name,
  arrivalDateTime: reservation.search_context.arrival_datetime.slice(0, -1),
  departureDateTime: reservation.search_context.departure_datetime.slice(0, -1),
  location: reservation.search_context.location.name,
  payment: {
    currency: reservation.payment.currency,
    totalPrice: reservation.payment.total_price,
    finalPrice: reservation.payment.final_price,
    coupon: reservation.payment.coupon_code && reservation.payment.discounts?.coupon?.amount ? {
      code: reservation.payment.coupon_code,
      discount: reservation.payment.discounts.coupon.amount
    } : void 0
  },
  status: reservation.status,
  cancellation: {
    noticeInHours: reservation.cancellation.notice_in_hours,
    refundPercentage: reservation.cancellation.refund_percentage
  }
});
const createParkingQuery = f$9.combineFetchers({
  getSearchResults,
  getSearchResult,
  initiateSearch,
  updateSearchResult,
  calculatePricing,
  createReservation,
  getReservation: getReservation$1,
  getReservations: getReservations$1,
  cancelReservation,
  resendNotification
});

const urlConfig$8 = {
  staging: {
    flightsUrl: "https://api-staging-internal.mozio.com/v2/flights/"
  },
  production: {
    flightsUrl: "https://api.mozio.com/v2/flights"
  },
  testing: {
    flightsUrl: "https://api-testing.mozio.com/v2/flights"
  }
};
const f$8 = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    lang: z.string(),
    "api-key": z.string()
  }
});
const dateToFormattedISO = (date) => {
  const offset = date.getTimezoneOffset() * 6e4;
  return new Date(date.valueOf() - offset).toISOString().slice(0, -1);
};
const FLIGHT_DIRECTION = {
  ARRIVAL: "arr",
  DEPARTURE: "dep"
};
const flightSchema = z.object({
  destination: z.string(),
  airline: z.object({
    airline_name: z.string(),
    airline_code: z.string()
  }),
  flight_code: z.string(),
  flight_number: z.string(),
  datetime: z.string(),
  origin: z.string(),
  time: z.string(),
  origin_terminal: z.string().nullable(),
  destination_terminal: z.string().nullable()
});
const queryFlights = f$8.fetcher(
  async ({ ctx, get }, {
    airlineCode,
    airportCode,
    flightNumber,
    flightDirection,
    dateTime
  }) => {
    const { flightsUrl } = urlConfig$8[ctx.env];
    if (!airlineCode) {
      return [];
    }
    const params = {
      airline_code: airlineCode,
      airport_code: airportCode,
      flight_direction: flightDirection,
      ...dateTime ? { pickup_datetime: dateToFormattedISO(dateTime) } : {},
      ...flightNumber ? { flight_number: flightNumber } : {}
    };
    const paramsString = new URLSearchParams(params).toString();
    const { data } = await get(
      `${flightsUrl}?${paramsString}`,
      z.object({
        flights: z.array(flightSchema)
      })
    );
    return data.flights.map((flight) => {
      const { airline, flight_code, flight_number, ...rest } = flight;
      return {
        ...rest,
        airlineCode: airline?.airline_code,
        airlineName: airline?.airline_name,
        flightCode: flight_code,
        flightNumber: flight_number,
        flightDateTime: new Date(flight.datetime),
        originTerminal: flight.origin_terminal ?? void 0,
        destinationTerminal: flight.destination_terminal ?? void 0
      };
    });
  }
);
const createFlightQuery = f$8.combineFetchers({
  queryFlights
});

const urlConfig$7 = {
  staging: {
    travelAgentUrl: "https://api-staging-internal.mozio.com/v2/travel_agent/"
  },
  production: {
    travelAgentUrl: "https://api.mozio.com/v2/travel_agent/"
  },
  testing: {
    travelAgentUrl: "https://api-testing.mozio.com/v2/travel_agent/"
  }
};
const f$7 = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    "api-key": z.string(),
    "Content-Type": z.string(),
    Authorization: z.string().optional(),
    Lang: z.string()
  }
});
const travelAgentSchema = z.union([
  z.object({
    company: z.object({
      name: z.string(),
      payout_to_master: z.boolean(),
      use_master_commission: z.boolean()
    }),
    tax_id: z.string().optional(),
    commission: z.number(),
    commission_percentage: z.number(),
    currency: z.string(),
    iata_code: z.string().optional().nullable(),
    abta_atol_code: z.string().optional(),
    trade_id: z.string().optional(),
    travel_agency_website: z.string().optional(),
    travel_agency_social_link: z.string().optional(),
    paypal_accounts: z.array(
      z.object({
        account_id: z.number(),
        paypal_email: z.string()
      })
    ).optional(),
    position: z.number(),
    travelers_settings_dict: z.object({
      dont_receive_cancellation: z.boolean(),
      dont_receive_tracking_reminder: z.boolean(),
      hide_payment_information: z.boolean(),
      dont_receive_oneway: z.boolean(),
      dont_receive_roundtrip: z.boolean(),
      dont_receive_marketing_offers: z.boolean()
    })
  }),
  authErrorSchema
]);
const getTravelAgent = f$7.fetcher(async ({ ctx, get }) => {
  const { travelAgentUrl } = urlConfig$7[ctx.env];
  const { data, response } = await get(travelAgentUrl, travelAgentSchema);
  if (response.status === 403) {
    throw new AuthError();
  }
  if ("code" in data) {
    throw new Error(data.message ?? "");
  }
  return {
    company: {
      name: data.company.name,
      payoutToMaster: data.company.payout_to_master,
      useMasterCommission: data.company.use_master_commission
    },
    taxId: data.tax_id,
    commissionType: data.commission_percentage > 0 ? "percentage" : "fixed",
    commissionFixed: data.commission,
    commissionPercentage: data.commission_percentage,
    currency: data.currency,
    iataCode: data.iata_code,
    abtaAtolCode: data.abta_atol_code,
    tradeId: data.trade_id,
    travelAgencyWebsite: data.travel_agency_website,
    travelAgencySocialLink: data.travel_agency_social_link,
    paypalAccounts: data.paypal_accounts?.map(
      (paypalAccount) => ({
        accountId: paypalAccount.account_id,
        paypalEmail: paypalAccount.paypal_email
      })
    ),
    position: data.position,
    travelersSettingsDict: {
      dontReceiveCancellation: data.travelers_settings_dict.dont_receive_cancellation,
      dontReceiveTrackingReminder: data.travelers_settings_dict.dont_receive_tracking_reminder,
      hidePaymentInformation: data.travelers_settings_dict.hide_payment_information,
      dontReceiveOneway: data.travelers_settings_dict.dont_receive_oneway,
      dontReceiveRoundtrip: data.travelers_settings_dict.dont_receive_roundtrip,
      dontReceiveMarketingOffers: data.travelers_settings_dict.dont_receive_marketing_offers
    }
  };
});
const customerProfileSchema = z.object({
  id: z.number(),
  profile_name: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string(),
  country_code: z.string(),
  country_code_name: z.string(),
  phone_number_national: z.string(),
  phone_number: z.string()
});
const customerProfileResponseSchema = z.union([
  z.object({
    count: z.number(),
    next: z.string().nullable(),
    previous: z.string().nullable(),
    results: z.array(customerProfileSchema)
  }),
  authErrorSchema
]);
const getCustomerProfiles = f$7.fetcher(
  async ({ ctx, get }, { page, pageSize }) => {
    const { travelAgentUrl } = urlConfig$7[ctx.env];
    const params = {
      ...page ? { page: page.toString() } : {},
      ...pageSize ? { page_size: pageSize.toString() } : {}
    };
    const paramsString = new URLSearchParams(params).toString();
    const { data, response } = await get(
      `${travelAgentUrl}profiles/?${paramsString}`,
      customerProfileResponseSchema
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if ("code" in data) {
      throw new Error(data.message ?? "");
    }
    return {
      count: data.count,
      next: data.next,
      previous: data.previous,
      results: data.results.map(
        (result) => ({
          id: result.id,
          profileName: result.profile_name,
          firstName: result.first_name,
          lastName: result.last_name,
          email: result.email,
          countryCode: result.country_code,
          countryCodeName: result.country_code_name,
          phoneNumberNational: result.phone_number_national,
          phoneNumber: result.phone_number
        })
      )
    };
  }
);
const updateTravelAgent = f$7.fetcher(
  async ({ ctx, patch }, travelAgent) => {
    const { travelAgentUrl } = urlConfig$7[ctx.env];
    const { data, response } = await patch(
      `${travelAgentUrl}`,
      travelAgentSchema,
      {
        abta_atol_code: travelAgent.abtaAtolCode,
        trade_id: travelAgent.tradeId,
        iata_code: travelAgent.iataCode,
        travel_agency_website: travelAgent.travelAgencyWebsite,
        travel_agency_social_link: travelAgent.travelAgencySocialLink,
        currency: travelAgent.currency,
        commission: travelAgent.commissionType ? travelAgent.commissionType === "fixed" ? Number(travelAgent.commissionFixed) : 0 : void 0,
        commission_percentage: travelAgent.commissionType ? travelAgent.commissionType === "percentage" ? Number(travelAgent.commissionPercentage) : 0 : void 0,
        ...travelAgent.taxId ? { tax_id: travelAgent.taxId } : {},
        ...travelAgent.travelersSettingsDict ? {
          travelers_settings_dict: {
            dont_receive_cancellation: travelAgent.travelersSettingsDict?.dontReceiveCancellation,
            dont_receive_tracking_reminder: travelAgent.travelersSettingsDict?.dontReceiveTrackingReminder,
            hide_payment_information: travelAgent.travelersSettingsDict?.hidePaymentInformation,
            dont_receive_oneway: travelAgent.travelersSettingsDict?.dontReceiveOneway,
            dont_receive_roundtrip: travelAgent.travelersSettingsDict?.dontReceiveRoundtrip,
            dont_receive_marketing_offers: travelAgent.travelersSettingsDict?.dontReceiveMarketingOffers
          }
        } : {}
      }
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if ("code" in data) {
      throw new Error(data.message ?? "");
    }
    return {
      company: {
        name: data.company.name,
        payoutToMaster: data.company.payout_to_master,
        useMasterCommission: data.company.use_master_commission
      },
      taxId: data.tax_id,
      commissionType: data.commission_percentage > 0 ? "percentage" : "fixed",
      commissionFixed: data.commission,
      commissionPercentage: data.commission_percentage,
      currency: data.currency,
      iataCode: data.iata_code,
      abtaAtolCode: data.abta_atol_code,
      tradeId: data.trade_id,
      travelAgencyWebsite: data.travel_agency_website,
      travelAgencySocialLink: data.travel_agency_social_link,
      paypalAccounts: data.paypal_accounts?.map(
        (paypalAccount) => ({
          accountId: paypalAccount.account_id,
          paypalEmail: paypalAccount.paypal_email
        })
      ),
      position: data.position,
      travelersSettingsDict: {
        dontReceiveCancellation: data.travelers_settings_dict.dont_receive_cancellation,
        dontReceiveTrackingReminder: data.travelers_settings_dict.dont_receive_tracking_reminder,
        hidePaymentInformation: data.travelers_settings_dict.hide_payment_information,
        dontReceiveOneway: data.travelers_settings_dict.dont_receive_oneway,
        dontReceiveRoundtrip: data.travelers_settings_dict.dont_receive_roundtrip,
        dontReceiveMarketingOffers: data.travelers_settings_dict.dont_receive_marketing_offers
      }
    };
  }
);
const errorSchema = z.object({
  message: z.string(),
  code: z.string(),
  user_message: z.string().nullable()
});
const errorResponseSchema = z.object({
  non_field_errors: z.array(errorSchema).optional(),
  profile_name: z.array(errorSchema).optional()
});
const createCustomerProfileResponseSchema = z.union([
  customerProfileSchema,
  authErrorSchema,
  errorResponseSchema
]);
const createCustomerProfile = f$7.fetcher(
  async ({ ctx, post }, {
    profileName,
    firstName,
    lastName,
    email,
    countryCodeName,
    phoneNumber
  }) => {
    const { travelAgentUrl } = urlConfig$7[ctx.env];
    const { data, response } = await post(
      `${travelAgentUrl}profiles/`,
      createCustomerProfileResponseSchema,
      {
        profile_name: profileName,
        first_name: firstName,
        last_name: lastName,
        email,
        country_code_name: countryCodeName,
        phone_number: phoneNumber
      }
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if ("code" in data) {
      throw new Error(data.message ?? "");
    }
    if (!("id" in data)) {
      const mergedErrors = [
        ...data.non_field_errors || [],
        ...data.profile_name || []
      ].map(
        (error) => ({
          message: error.message,
          code: error.code,
          userMessage: error.user_message
        })
      );
      return {
        errors: mergedErrors
      };
    }
    return {
      id: data.id,
      profileName: data.profile_name,
      firstName: data.first_name,
      lastName: data.last_name,
      email: data.email,
      countryCode: data.country_code,
      countryCodeName: data.country_code_name,
      phoneNumberNational: data.phone_number_national,
      phoneNumber: data.phone_number
    };
  }
);
const addPaypalAccount = f$7.fetcher(
  async ({ ctx, post }, { paypalEmail }) => {
    const { travelAgentUrl } = urlConfig$7[ctx.env];
    const { data, response } = await post(
      `${travelAgentUrl}paypal_account/`,
      z.object({
        account_id: z.number(),
        paypal_email: z.string()
      }),
      {
        paypal_email: paypalEmail
      }
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    return {
      accountId: data.account_id,
      paypalEmail: data.paypal_email
    };
  }
);
const updateCustomerProfileResponseSchema = z.union([
  customerProfileSchema,
  authErrorSchema,
  errorResponseSchema
]);
const updateCustomerProfile = f$7.fetcher(
  async ({ ctx, patch }, {
    id,
    profileName,
    firstName,
    lastName,
    email,
    countryCodeName,
    phoneNumber
  }) => {
    const { travelAgentUrl } = urlConfig$7[ctx.env];
    const { data, response } = await patch(
      `${travelAgentUrl}profiles/${id}/`,
      updateCustomerProfileResponseSchema,
      {
        profile_name: profileName,
        first_name: firstName,
        last_name: lastName,
        email,
        country_code_name: countryCodeName,
        phone_number: phoneNumber
      }
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if ("code" in data) {
      throw new Error(data.message ?? "");
    }
    if (!("id" in data)) {
      const mergedErrors = [
        ...data.non_field_errors || [],
        ...data.profile_name || []
      ].map(
        (error) => ({
          message: error.message,
          code: error.code,
          userMessage: error.user_message
        })
      );
      return {
        errors: mergedErrors
      };
    }
    return {
      id: data.id,
      profileName: data.profile_name,
      firstName: data.first_name,
      lastName: data.last_name,
      email: data.email,
      countryCode: data.country_code,
      countryCodeName: data.country_code_name,
      phoneNumberNational: data.phone_number_national,
      phoneNumber: data.phone_number
    };
  }
);
const updateCompanyResponseSchema = z.union([
  travelAgentSchema,
  authErrorSchema,
  z.object({
    name: z.array(errorSchema).optional()
  }),
  z.array(errorSchema)
]);
const updateCompany = f$7.fetcher(
  async ({ ctx, patch }, { name, payoutToMaster, useMasterCommission }) => {
    const { travelAgentUrl } = urlConfig$7[ctx.env];
    const { data, response } = await patch(
      `${travelAgentUrl}company/`,
      updateCompanyResponseSchema,
      {
        ...name ? { name } : {},
        payout_to_master: payoutToMaster,
        use_master_commission: useMasterCommission
      }
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if ("code" in data) {
      throw new Error(data.message ?? "");
    }
    if (!("company" in data)) {
      if (Array.isArray(data)) {
        const mergedErrors2 = data.map(
          (error) => ({
            message: error.message,
            code: error.code,
            userMessage: error.user_message
          })
        );
        return {
          errors: mergedErrors2
        };
      }
      const mergedErrors = [...data.name || []].map(
        (error) => ({
          message: error.message,
          code: error.code,
          userMessage: error.user_message
        })
      );
      return {
        errors: mergedErrors
      };
    }
    return {
      company: {
        name: data.company.name,
        payoutToMaster: data.company.payout_to_master,
        useMasterCommission: data.company.use_master_commission
      },
      taxId: data.tax_id,
      commissionType: data.commission_percentage > 0 ? "percentage" : "fixed",
      commissionFixed: data.commission,
      commissionPercentage: data.commission_percentage,
      currency: data.currency,
      iataCode: data.iata_code,
      abtaAtolCode: data.abta_atol_code,
      tradeId: data.trade_id,
      travelAgencyWebsite: data.travel_agency_website,
      travelAgencySocialLink: data.travel_agency_social_link,
      paypalAccounts: data.paypal_accounts?.map(
        (paypalAccount) => ({
          accountId: paypalAccount.account_id,
          paypalEmail: paypalAccount.paypal_email
        })
      ),
      position: data.position,
      travelersSettingsDict: {
        dontReceiveCancellation: data.travelers_settings_dict.dont_receive_cancellation,
        dontReceiveTrackingReminder: data.travelers_settings_dict.dont_receive_tracking_reminder,
        hidePaymentInformation: data.travelers_settings_dict.hide_payment_information,
        dontReceiveOneway: data.travelers_settings_dict.dont_receive_oneway,
        dontReceiveRoundtrip: data.travelers_settings_dict.dont_receive_roundtrip,
        dontReceiveMarketingOffers: data.travelers_settings_dict.dont_receive_marketing_offers
      }
    };
  }
);
const deleteCustomerProfile = f$7.fetcher(
  async ({ ctx, headers }, id) => {
    const { travelAgentUrl } = urlConfig$7[ctx.env];
    const response = await fetch(`${travelAgentUrl}profiles/${id}`, {
      method: "DELETE",
      headers: {
        ...headers,
        Authorization: headers.Authorization ?? ""
      }
    });
    if (response.status === 403) {
      throw new AuthError();
    }
    if (response.status !== 204) {
      throw new Error("Failed to delete travel agent profile");
    }
    return id;
  }
);
const deletePaypalAccount = f$7.fetcher(
  async ({ ctx, headers }, id) => {
    const { travelAgentUrl } = urlConfig$7[ctx.env];
    if (!headers.Authorization) {
      throw new AuthError();
    }
    const response = await fetch(`${travelAgentUrl}paypal_account/${id}`, {
      method: "DELETE",
      headers: {
        ...headers,
        Authorization: headers.Authorization
      }
    });
    if (response.status === 403) {
      throw new AuthError();
    }
    if (response.ok) {
      return id;
    }
    throw new Error("Something went wrong");
  }
);
const withdrawalsResponseSchema = z.object({
  withdraw_date: z.string().nullable()
});
const getWithdrawalDate = f$7.fetcher(
  async ({ ctx, get }, { year, month }) => {
    const { travelAgentUrl } = urlConfig$7[ctx.env];
    const { data, response } = await get(
      `${travelAgentUrl}withdrawals/${year}/${month}/`,
      withdrawalsResponseSchema
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    return {
      withdrawDate: data.withdraw_date
    };
  }
);
const withdrawResponseSchema = z.object({
  account_id: z.number(),
  paypal_email: z.string()
});
const withdrawOutputSchema = z.union([withdrawResponseSchema, errorSchema]);
const withdraw = f$7.fetcher(
  async ({ ctx, post }, input) => {
    const { travelAgentUrl } = urlConfig$7[ctx.env];
    const { data } = await post(
      `${travelAgentUrl}paypal_account/${input.accountId}/withdraw/`,
      withdrawOutputSchema,
      {
        month: input.month,
        year: input.year
      }
    );
    if ("code" in data) {
      return {
        message: data.message || "",
        code: data.code,
        userMessage: data.user_message || ""
      };
    }
    return {
      accountId: data.account_id,
      paypalEmail: data.paypal_email
    };
  }
);
const stripeWithdrawResponseSchema = z.object({
  success: z.boolean()
});
const stripeWithdrawOutputSchema = z.union([
  stripeWithdrawResponseSchema,
  errorSchema
]);
const stripeWithdraw = f$7.fetcher(
  async ({ ctx, post }, input) => {
    const { travelAgentUrl } = urlConfig$7[ctx.env];
    const { data } = await post(
      `${travelAgentUrl}stripe-account/withdraw/`,
      stripeWithdrawOutputSchema,
      {
        month: input.month,
        year: input.year
      }
    );
    if ("code" in data) {
      return {
        message: data.message || "",
        code: data.code,
        userMessage: data.user_message || ""
      };
    }
    return {
      success: data.success
    };
  }
);
const withdrawalSchema = z.object({
  withdraw_date: z.string(),
  sales_year: z.number(),
  sales_month: z.number(),
  commission_total: z.string(),
  transfer_account: z.string(),
  transaction_id: z.string()
});
const getWithdrawalsResponseSchema = z.object({
  withdrawals: z.array(withdrawalSchema)
});
const getWithdrawals = f$7.fetcher(
  async ({ ctx, get }) => {
    const { travelAgentUrl } = urlConfig$7[ctx.env];
    const { data, response } = await get(
      `${travelAgentUrl}withdrawals/`,
      getWithdrawalsResponseSchema
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    return {
      withdrawals: data.withdrawals.map(
        (withdrawal) => ({
          withdrawDate: withdrawal.withdraw_date,
          salesYear: withdrawal.sales_year,
          salesMonth: withdrawal.sales_month,
          commissionTotal: withdrawal.commission_total,
          transferAccount: withdrawal.transfer_account,
          transactionId: withdrawal.transaction_id
        })
      )
    };
  }
);
const stripeAccountSessionResponseSchema = z.object({
  client_secret: z.string()
});
const getStripeAccountSession = f$7.fetcher(
  async ({ ctx, get }) => {
    const { travelAgentUrl } = urlConfig$7[ctx.env];
    const { data, response } = await get(
      `${travelAgentUrl}stripe-account/session/`,
      stripeAccountSessionResponseSchema
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    return data.client_secret;
  }
);
const stripeAccountStatusResponseSchema = z.object({
  details_submitted: z.boolean(),
  charges_enabled: z.boolean(),
  payouts_enabled: z.boolean(),
  requirements: z.object({
    currently_due: z.array(z.string()),
    eventually_due: z.array(z.string()),
    past_due: z.array(z.string())
  })
});
const getStripeAccountStatus = f$7.fetcher(
  async ({ ctx, get }) => {
    const { travelAgentUrl } = urlConfig$7[ctx.env];
    const { data, response } = await get(
      `${travelAgentUrl}stripe-account/status/`,
      stripeAccountStatusResponseSchema
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    return {
      detailsSubmitted: data.details_submitted,
      chargesEnabled: data.charges_enabled,
      payoutsEnabled: data.payouts_enabled,
      requirements: {
        currentlyDue: data.requirements.currently_due,
        eventuallyDue: data.requirements.eventually_due,
        pastDue: data.requirements.past_due
      }
    };
  }
);
const updateStripeAccountCountryResponseSchema = z.object({
  success: z.boolean()
});
const updateStripeAccountCountryOutputSchema = z.union([
  updateStripeAccountCountryResponseSchema,
  errorSchema
]);
const updateStripeAccountCountry = f$7.fetcher(
  async ({ ctx, post }, input) => {
    const { travelAgentUrl } = urlConfig$7[ctx.env];
    const { data } = await post(
      `${travelAgentUrl}stripe-account/`,
      updateStripeAccountCountryOutputSchema,
      {
        country_code: input.countryCode.toUpperCase()
      }
    );
    if ("code" in data) {
      return {
        message: data.message || "",
        code: data.code,
        userMessage: data.user_message || ""
      };
    }
    return {
      success: data.success
    };
  }
);
const companyAutocompleteItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  company_code: z.string()
}).passthrough();
const companyAutocompleteResponseSchema = z.union([
  z.array(companyAutocompleteItemSchema),
  authErrorSchema
]);
const getCompanyAutocomplete = f$7.fetcher(
  async ({ ctx, get }, { query }) => {
    const { travelAgentUrl } = urlConfig$7[ctx.env];
    const params = new URLSearchParams({ query }).toString();
    const { data, response } = await get(
      `${travelAgentUrl}company/autocomplete/?${params}`,
      companyAutocompleteResponseSchema
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if ("code" in data) {
      throw new Error(data.message ?? "");
    }
    return data.map(
      (item) => ({
        id: item.id,
        name: item.name,
        companyCode: item.company_code
      })
    );
  }
);
const createTravelAgentResponseSchema = z.unknown();
const createTravelAgent = f$7.fetcher(
  async ({ ctx, post }, input) => {
    const { travelAgentUrl } = urlConfig$7[ctx.env];
    const { data, response } = await post(
      travelAgentUrl,
      createTravelAgentResponseSchema,
      {
        email: input.email,
        password: input.password,
        first_name: input.firstName,
        last_name: input.lastName,
        country_code_name: input.countryCodeName,
        phone_number: input.phoneNumber,
        language: input.language,
        currency: input.currency,
        country: input.country,
        ...input.companyCode ? { company_code: input.companyCode } : input.companyName ? { company_name: input.companyName } : {},
        ...input.referralProgramCode ? { referral_program_code: input.referralProgramCode } : {}
      }
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if (response.status >= 200 && response.status < 300) {
      return { success: true };
    }
    const authErrorParsed = authErrorSchema.safeParse(data);
    if (authErrorParsed.success) {
      throw new Error(authErrorParsed.data.message ?? "");
    }
    const errors = [];
    if (typeof data === "object" && data !== null) {
      for (const fieldErrors of Object.values(data)) {
        if (!Array.isArray(fieldErrors)) continue;
        for (const fieldError of fieldErrors) {
          if (typeof fieldError !== "object" || fieldError === null) continue;
          const errorRecord = fieldError;
          if (typeof errorRecord.message !== "string") continue;
          const userMessage = typeof errorRecord.user_message === "string" ? errorRecord.user_message : (
            // eslint-disable-next-line unicorn/no-null
            null
          );
          errors.push({
            message: errorRecord.message,
            code: typeof errorRecord.code === "string" ? errorRecord.code : "",
            userMessage
          });
        }
      }
    }
    return { success: false, errors };
  }
);
const createTravelAgentQuery = f$7.combineFetchers({
  getTravelAgent,
  getCustomerProfiles,
  updateTravelAgent,
  createCustomerProfile,
  updateCustomerProfile,
  updateCompany,
  deleteCustomerProfile,
  addPaypalAccount,
  deletePaypalAccount,
  getWithdrawalDate,
  withdraw,
  getWithdrawals,
  getStripeAccountSession,
  getStripeAccountStatus,
  stripeWithdraw,
  updateStripeAccountCountry,
  getCompanyAutocomplete,
  createTravelAgent
});

const urlConfig$6 = {
  staging: {
    createRatingUrl: "https://api-staging-internal.mozio.com/v2/data-entry/ratings/external/",
    reservationBaseUrl: "https://api-staging-internal.mozio.com/v2/reservations",
    trackingPageLayoutUrl: "https://api-staging-internal.mozio.com/v2/partners/tracking-page-layout",
    trackingServiceTrackingsBaseUrl: "https://tracking-staging.mozio.com/v1/trackings",
    directionsUrlV2: "https://api-staging-internal.mozio.com/v2/locations/directions/"
  },
  production: {
    createRatingUrl: "https://api.mozio.com/v2/data-entry/ratings/external/",
    reservationBaseUrl: "https://api.mozio.com/v2/reservations",
    trackingPageLayoutUrl: "https://api.mozio.com/v2/partners/tracking-page-layout",
    trackingServiceTrackingsBaseUrl: "https://tracking-api.mozio.com/v1/trackings",
    directionsUrlV2: "https://api.mozio.com/v2/locations/directions/"
  },
  testing: {
    createRatingUrl: "https://api-testing.mozio.com/v2/data-entry/ratings/external/",
    reservationBaseUrl: "https://api-testing.mozio.com/v2/reservations",
    trackingPageLayoutUrl: "https://api-testing.mozio.com/v2/partners/tracking-page-layout",
    trackingServiceTrackingsBaseUrl: "https://tracking-api-testing.mozio.com/v1/trackings",
    directionsUrlV2: "https://api-testing.mozio.com/v2/locations/directions/"
  }
};
const f$6 = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    "api-key": z.string(),
    "Content-Type": z.string(),
    Lang: z.string(),
    ref: z.string().nullish()
  }
});
const createRating = f$6.fetcher(
  async ({ ctx, post }, { score, description, rating_token }) => {
    const { createRatingUrl } = urlConfig$6[ctx.env];
    const { data } = await post(
      createRatingUrl,
      z.object({
        score: z.number()
      }),
      {
        score,
        description,
        rating_token
      }
    );
    return {
      score: data.score
    };
  }
);
const trackingVehicleDriverSchema = z.object({
  name: z.string(),
  phone: z.string().nullish()
}).transform((d) => ({
  name: d.name,
  phone: d.phone ?? void 0
}));
const trackingVehicleBodySchema = z.object({
  license_plate: z.string().nullish(),
  make: z.string().nullish(),
  model: z.string().nullish(),
  color: z.string().nullish(),
  plate: z.string().nullish(),
  manufacturer: z.string().nullish(),
  image_url: z.string().nullish()
}).passthrough();
const vehicleInfoSchema = z.object({
  status: z.string(),
  driver: trackingVehicleDriverSchema.nullable(),
  vehicle: z.preprocess(
    (val) => val ?? void 0,
    z.union([z.undefined(), trackingVehicleBodySchema])
  ).transform(
    (raw) => {
      if (raw === void 0) return void 0;
      return {
        manufacturer: raw.make ?? raw.manufacturer ?? void 0,
        model: raw.model ?? void 0,
        color: raw.color ?? void 0,
        plate: raw.plate ?? void 0,
        imageUrl: raw.image_url ?? void 0
      };
    }
  )
});
const locationSchema$1 = z.object({
  formatted_address: z.string(),
  lat: z.number(),
  lng: z.number()
});
const reservationSchema = z.object({
  confirmation_number: z.string(),
  pickup_instructions: z.string(),
  mobile_pickup_instructions: z.string().nullable(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  voyage: z.object({
    departure_datetime: z.string(),
    start_location: locationSchema$1,
    end_location: locationSchema$1,
    provider_name: z.string()
  }),
  provider: z.object({
    phone_number: z.string().nullish()
  }).nullish(),
  support_numbers: z.array(z.string()).optional(),
  reservation_type: z.string().optional()
}).transform(
  (r) => ({
    confirmationNumber: r.confirmation_number,
    pickupInstructions: r.pickup_instructions,
    mobilePickupInstructions: r.mobile_pickup_instructions,
    firstName: r.first_name ?? void 0,
    lastName: r.last_name ?? void 0,
    voyage: {
      departureDatetime: r.voyage.departure_datetime,
      startLocation: r.voyage.start_location,
      endLocation: r.voyage.end_location,
      providerName: r.voyage.provider_name
    },
    providerPhone: r.provider?.phone_number ?? void 0,
    supportNumbers: r.support_numbers ?? [],
    reservationType: r.reservation_type
  })
);
const getVehicleInfo = f$6.fetcher(
  async ({ ctx, get }, reservationHashId) => {
    const { reservationBaseUrl } = urlConfig$6[ctx.env];
    const { data } = await get(
      `${reservationBaseUrl}/${reservationHashId}/vehicle/`,
      vehicleInfoSchema
    );
    return data;
  }
);
const getReservation = f$6.fetcher(
  async ({ ctx, get }, reservationHashId) => {
    const { reservationBaseUrl } = urlConfig$6[ctx.env];
    const { data } = await get(
      `${reservationBaseUrl}/${reservationHashId}/`,
      reservationSchema
    );
    return data;
  }
);
const trackingPageLayoutSchema = z.object({
  show_header: z.boolean(),
  show_footer: z.boolean()
}).transform((l) => ({
  showHeader: l.show_header,
  showFooter: l.show_footer
}));
const getTrackingPageLayout = f$6.fetcher(
  async ({ ctx, get }) => {
    const { trackingPageLayoutUrl } = urlConfig$6[ctx.env];
    const { data } = await get(
      `${trackingPageLayoutUrl}/`,
      trackingPageLayoutSchema
    );
    return data;
  }
);
const trackingLatestEventSchema = z.object({
  lat: z.number().nullish().transform((v) => v ?? 0),
  lng: z.number().nullish().transform((v) => v ?? 0),
  status: z.string().nullish().transform((v) => v ?? ""),
  timestamp: z.string().nullish().transform((v) => v ?? ""),
  /** Degrees clockwise from north; omitted or null on older payloads. */
  bearing: z.number().nullish()
});
const directionsV2ResponseSchema = z.object({
  segments: z.array(
    z.object({
      category: z.string(),
      polyline_points: z.string(),
      distance: z.number(),
      duration: z.number()
    })
  )
});
const getDirectionsV2 = f$6.fetcher(
  async ({ post, ctx }, input) => {
    const { directionsUrlV2 } = urlConfig$6[ctx.env];
    const segments = "segments" in input ? input.segments : [
      {
        category: input.category,
        start: input.start,
        end: input.end
      }
    ];
    const { data } = await post(directionsUrlV2, directionsV2ResponseSchema, {
      segments
    });
    return data;
  }
);
const getTrackingLatestEvents = f$6.fetcher(
  async ({ ctx, get }, trackingId) => {
    const { trackingServiceTrackingsBaseUrl } = urlConfig$6[ctx.env];
    try {
      const { data } = await get(
        `${trackingServiceTrackingsBaseUrl}/${encodeURIComponent(trackingId)}/events/latest`,
        trackingLatestEventSchema
      );
      return data;
    } catch (error) {
      console.error({ error });
      return void 0;
    }
  }
);
const createTrackingQuery = f$6.combineFetchers({
  createRating,
  getVehicleInfo,
  getReservation,
  getTrackingPageLayout,
  getTrackingLatestEvents,
  getDirectionsV2
});

const urlConfig$5 = {
  staging: {
    userUrl: "https://api-staging-internal.mozio.com/v2/auth/users",
    reservationUrl: "https://api-staging-internal.mozio.com/v2/reservations"
  },
  production: {
    userUrl: "https://api.mozio.com/v2/auth/users",
    reservationUrl: "https://api.mozio.com/v2/reservations"
  },
  testing: {
    userUrl: "https://api-testing.mozio.com/v2/auth/users",
    reservationUrl: "https://api-testing.mozio.com/v2/reservations"
  }
};
const f$5 = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    "api-key": z.string(),
    "Content-Type": z.string(),
    Authorization: z.string().optional(),
    Lang: z.string()
  }
});
const chatTokenSchema = z.object({
  token: z.string()
});
const getChatToken = f$5.fetcher(async ({ ctx, get }) => {
  const { userUrl } = urlConfig$5[ctx.env];
  const { data, response } = await get(
    `${userUrl}/me/chat-token/`,
    z.union([chatTokenSchema, authErrorSchema])
  );
  if (response.status === 403) {
    throw new AuthError();
  }
  if ("code" in data) {
    throw new Error(data.message ?? "");
  }
  return data.token;
});
const chatChannelSchema = z.object({
  channel_type: z.string(),
  channel_id: z.string()
});
const getChatChannel = f$5.fetcher(
  async ({ ctx, get }, { reservationHashedId }) => {
    const { reservationUrl } = urlConfig$5[ctx.env];
    const { data, response } = await get(
      `${reservationUrl}/${reservationHashedId}/chat-channel/`,
      z.union([chatChannelSchema, authErrorSchema])
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if ("code" in data) {
      throw new Error(data.message ?? "");
    }
    return {
      channelType: data.channel_type,
      channelId: data.channel_id
    };
  }
);
const getGuestUserId = f$5.fetcher(
  async ({ ctx, get }, { reservationHashedId }) => {
    const { reservationUrl } = urlConfig$5[ctx.env];
    const { data, response } = await get(
      `${reservationUrl}/${reservationHashedId}/chat-channel/guest`,
      z.object({
        user_id: z.string(),
        token: z.string()
      })
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    return {
      userId: data.user_id,
      token: data.token
    };
  }
);
const createChatQuery = f$5.combineFetchers({
  getChatToken,
  getChatChannel,
  getGuestUserId
});

const urlConfig$4 = {
  staging: {
    engageUrl: "https://api-staging-internal.mozio.com/v2/engage"
  },
  production: {
    engageUrl: "https://api.mozio.com/v2/engage"
  },
  testing: {
    engageUrl: "https://api-testing.mozio.com/v2/engage"
  }
};
const f$4 = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    "api-key": z.string(),
    "Content-Type": z.string(),
    Lang: z.string()
  }
});
const engageUserCaptureResponseSchema = z.object({
  id: z.number().optional(),
  email: z.string(),
  url: z.string(),
  provider_name: z.string().optional(),
  session_id: z.string().optional(),
  campaign: z.union([z.string(), z.number()]).optional()
}).passthrough();
const captureEngageUser = f$4.fetcher(
  async ({ ctx, post }, {
    email,
    url,
    provider_name,
    session_id,
    first_name,
    last_name
  }) => {
    const { engageUrl } = urlConfig$4[ctx.env];
    await post(`${engageUrl}/`, engageUserCaptureResponseSchema, {
      email,
      url,
      provider_name,
      session_id,
      first_name,
      last_name,
      campaign: 5
    });
  }
);
const createEngageQuery = f$4.combineFetchers({
  captureEngageUser
});

const urlConfig$3 = {
  staging: {
    kiwiUrl: "https://api-staging-internal.mozio.com/v2/proxy/kiwi-com/"
  },
  production: {
    kiwiUrl: "https://api.mozio.com/v2/proxy/kiwi-com/"
  },
  testing: {
    kiwiUrl: "https://api-testing-internal.mozio.com/v2/proxy/kiwi-com/"
  }
};
const f$3 = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    "KW-Marketplace-Token": z.string(),
    "Content-Type": z.string(),
    "api-key": z.string()
  }
});
const contactSchema = z.object({
  email: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  phone: z.string().nullable()
});
const timeSchema = z.object({
  local: z.string()
});
const placeSchema = z.object({
  city: z.string()
});
const stationSchema = z.object({
  code: z.string()
});
const locationSchema = z.object({
  place: placeSchema.optional(),
  station: stationSchema,
  time: timeSchema
});
const carrierSchema = z.object({
  code: z.string(),
  segment_code: z.string()
});
const segmentSchema = z.object({
  arrival: locationSchema,
  carrier: carrierSchema,
  departure: locationSchema,
  sector: z.number()
});
const sectorSchema = z.object({
  arrival: locationSchema,
  departure: locationSchema,
  id: z.number()
});
const passengerSchema = z.unknown();
const itinerarySchema = z.object({
  itinerary_type: z.enum(["one_way", "return", "multicity", "nomad"]),
  sectors: z.array(sectorSchema),
  segments: z.array(segmentSchema)
});
const bookingSchema = z.object({
  itinerary: itinerarySchema,
  passengers: z.array(passengerSchema)
});
const userContextSchema = z.object({
  contact: contactSchema,
  bookings: z.array(bookingSchema)
});
const stripTimezoneOffset = (datetime) => datetime.replace(/[+-]\d{2}:\d{2}$/, "");
const extractDialCode = (phone) => {
  if (!phone || !phone.startsWith("+")) return void 0;
  const match = phone.match(/^\+(\d{1,4})[\s-]/);
  if (match) return `+${match[1]}`;
  const fallback = phone.match(/^\+(\d{1,4})/);
  return fallback ? `+${fallback[1]}` : void 0;
};
const stripDialCode = (phone, dialCode) => {
  if (!phone) return "";
  const digitsOnly = phone.replace(/\D/g, "");
  if (!dialCode) return digitsOnly;
  const dialCodeDigits = dialCode.replace(/\D/g, "");
  return digitsOnly.startsWith(dialCodeDigits) ? digitsOnly.slice(dialCodeDigits.length) : digitsOnly;
};
const getMode = (itineraryType) => {
  if (!itineraryType) return "one_way";
  return itineraryType === "return" ? "round_trip" : "one_way";
};
const getUserContext = f$3.fetcher(
  async ({ ctx, get }) => {
    const { kiwiUrl } = urlConfig$3[ctx.env];
    const { data, response } = await get(
      `${kiwiUrl}context`,
      userContextSchema
    );
    if (response.status === 403 || response.status === 401) {
      throw new AuthError();
    }
    const firstBooking = data.bookings[0];
    const sectors = firstBooking?.itinerary.sectors ?? [];
    const segments = firstBooking?.itinerary.segments ?? [];
    const outboundSegments = segments.filter((s) => s.sector === 0);
    const arrivalSegment = outboundSegments[outboundSegments.length - 1];
    const departureSegment = segments.find(
      (s) => s.sector === 1
    );
    const dialCode = extractDialCode(data.contact.phone);
    return {
      firstName: data.contact.first_name,
      lastName: data.contact.last_name,
      email: data.contact.email,
      phoneNumber: stripDialCode(data.contact.phone, dialCode),
      dialCode,
      pickupLocation: arrivalSegment?.arrival.station.code,
      destinationCity: sectors[0]?.arrival.place?.city,
      pickupDatetime: arrivalSegment ? stripTimezoneOffset(arrivalSegment.arrival.time.local) : void 0,
      mode: getMode(firstBooking?.itinerary.itinerary_type),
      numPassengers: firstBooking?.passengers.length ?? 1,
      returnPickupDatetime: departureSegment ? stripTimezoneOffset(departureSegment.departure.time.local) : void 0,
      airline: arrivalSegment?.carrier.code,
      flightNumber: arrivalSegment?.carrier.segment_code,
      airline2: departureSegment?.carrier.code,
      flightNumber2: departureSegment?.carrier.segment_code
    };
  }
);
const createBasketResponseSchema = z.object({
  shopping_basket_id: z.string()
});
const createBasket = f$3.fetcher(
  async ({ ctx, post }, { is_sandbox, line_items, cart_id }) => {
    const { kiwiUrl } = urlConfig$3[ctx.env];
    const { data, response } = await post(
      `${kiwiUrl}checkout`,
      createBasketResponseSchema,
      {
        is_sandbox: is_sandbox ?? false,
        line_items
      }
    );
    if (response.status === 403 || response.status === 401) {
      throw new AuthError();
    }
    return {
      shoppingBasketId: data.shopping_basket_id,
      cartId: cart_id
    };
  }
);
const createTransferKiwiQuery = f$3.combineFetchers({
  getUserContext,
  createBasket
});

const urlConfig$2 = {
  staging: {
    referralUrl: "https://api-staging-internal.mozio.com/v2/referrals/"
  },
  production: {
    referralUrl: "https://api.mozio.com/v2/referrals/"
  },
  testing: {
    referralUrl: "https://api-testing.mozio.com/v2/referrals/"
  }
};
const f$2 = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    "api-key": z.string(),
    "Content-Type": z.string(),
    Authorization: z.string().optional(),
    Lang: z.string()
  }
});
const referralItemSchema = z.object({
  referred_name: z.string(),
  referred_email: z.string(),
  status: z.string(),
  confirmed_at: z.string().nullable(),
  coupon_earned: z.boolean()
});
const referralCouponSchema = z.object({
  code: z.string(),
  percentage: z.number(),
  expires_at: z.string()
});
const dashboardSchema = z.object({
  referral_code: z.string(),
  stats: z.object({
    total_referrals: z.number(),
    confirmed_referrals: z.number(),
    available_rewards: z.number(),
    total_coupons_earned: z.number(),
    total_saved: z.number()
  }),
  level: z.object({
    percentage: z.number(),
    confirmed_last_6_months: z.number(),
    next_threshold: z.number().nullable(),
    next_percentage: z.number().nullable(),
    progress_percent: z.number()
  }),
  referral_coupons: z.array(referralCouponSchema),
  available_coupons: z.array(referralCouponSchema),
  referrals: z.array(referralItemSchema)
});
const dashboardResponseSchema = z.union([dashboardSchema, authErrorSchema]);
const getDashboard = f$2.fetcher(
  async ({ ctx, get }) => {
    const { referralUrl } = urlConfig$2[ctx.env];
    const { data, response } = await get(
      `${referralUrl}dashboard/`,
      dashboardResponseSchema
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if ("code" in data) {
      throw new Error(data.message ?? "");
    }
    return {
      referralCode: data.referral_code,
      stats: {
        totalReferrals: data.stats.total_referrals,
        confirmedReferrals: data.stats.confirmed_referrals,
        availableRewards: data.stats.available_rewards,
        totalCouponsEarned: data.stats.total_coupons_earned,
        totalSaved: data.stats.total_saved
      },
      level: {
        percentage: data.level.percentage,
        confirmedLast6Months: data.level.confirmed_last_6_months,
        nextThreshold: data.level.next_threshold,
        nextPercentage: data.level.next_percentage,
        progressPercent: data.level.progress_percent
      },
      referralCoupons: data.referral_coupons.map(
        (c) => ({
          code: c.code,
          percentage: c.percentage,
          expiresAt: c.expires_at
        })
      ),
      availableCoupons: data.available_coupons.map(
        (c) => ({
          code: c.code,
          percentage: c.percentage,
          expiresAt: c.expires_at
        })
      ),
      referrals: data.referrals.map(
        (ref) => ({
          referredName: ref.referred_name,
          referredEmail: ref.referred_email,
          status: ref.status,
          confirmedAt: ref.confirmed_at,
          couponEarned: ref.coupon_earned
        })
      )
    };
  }
);
const createReferralQuery = f$2.combineFetchers({
  getDashboard
});

const urlConfig$1 = {
  staging: {
    reservationsUrl: "https://api-staging-internal.mozio.com/v2/partners-stats/reservations",
    reservationUrl: "https://api-staging-internal.mozio.com/v2/reservations",
    travelAgentsUrl: "https://api-staging-internal.mozio.com/v2/travel_agent/agents",
    partnersUrl: "https://api-staging-internal.mozio.com/v2/partners-stats/partners"
  },
  production: {
    reservationsUrl: "https://api.mozio.com/v2/partners-stats/reservations",
    reservationUrl: "https://api.mozio.com/v2/reservations",
    travelAgentsUrl: "https://api.mozio.com/v2/travel_agent/agents",
    partnersUrl: "https://api.mozio.com/v2/partners-stats/partners"
  },
  testing: {
    reservationsUrl: "https://api-testing.mozio.com/v2/partners-stats/reservations",
    reservationUrl: "https://api-testing.mozio.com/v2/reservations",
    travelAgentsUrl: "https://api-testing.mozio.com/v2/travel_agent/agents",
    partnersUrl: "https://api-testing.mozio.com/v2/partners-stats/partners"
  }
};
const f$1 = new FetcherClient({
  ctx: z.object({
    env: envSchema
  }),
  headers: {
    "api-key": z.string(),
    authorization: z.string()
  }
});
const apiErrorSchema = z.object({
  code: z.string(),
  message: z.string().optional().nullable(),
  user_message: z.string().optional().nullable()
});
const PAGE_SIZE = 20;
const reservationsResponseSchema = z.union([
  z.object({
    count: z.number(),
    results: z.array(
      z.object({
        id: z.coerce.string(),
        confirmation_number: z.string(),
        user_email: z.string().optional().nullable(),
        first_name: z.string(),
        last_name: z.string(),
        timestamp: z.string(),
        pickup_datetime: z.string(),
        start_location: z.object({
          custom_name: z.string().optional().nullable(),
          formatted_address: z.string().optional().nullable(),
          full_address: z.string().optional().nullable()
        }).passthrough().nullable(),
        end_location: z.object({
          custom_name: z.string().optional().nullable(),
          formatted_address: z.string().optional().nullable(),
          full_address: z.string().optional().nullable()
        }).passthrough().nullable(),
        amount_paid: z.object({
          display: z.string(),
          compact: z.string().optional()
        }),
        partner_ref: z.string().nullable(),
        cancelled: z.boolean(),
        status: z.string(),
        rebooked_id: z.coerce.string().nullable(),
        tracking_url: z.string().nullable(),
        agent: z.object({
          first_name: z.string(),
          last_name: z.string()
        }).optional().nullable()
      })
    )
  }),
  apiErrorSchema
]);
const getReservations = f$1.fetcher(
  async ({ ctx, get }, {
    page = 1,
    search,
    partnerRefs,
    status,
    timestampFrom,
    timestampTo,
    agents,
    ordering
  }) => {
    const { reservationsUrl } = urlConfig$1[ctx.env];
    const params = new URLSearchParams({ page: page.toString() });
    if (search) params.set("search", search);
    if (partnerRefs) {
      for (const ref of partnerRefs) {
        params.append("partner-ref", ref);
      }
    }
    if (status) params.set("status", status);
    if (timestampFrom) params.set("timestamp__from", timestampFrom);
    if (timestampTo) params.set("timestamp__to", timestampTo);
    if (agents) {
      for (const id of agents) {
        params.append("agent", id);
      }
    }
    if (ordering) params.set("ordering", ordering);
    const paramsString = params.toString();
    const { data, response } = await get(
      `${reservationsUrl}/?${paramsString}`,
      reservationsResponseSchema
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if ("code" in data) {
      throw new Error(data.message ?? "Unknown error");
    }
    return {
      count: data.count,
      pages: Math.ceil(data.count / PAGE_SIZE),
      results: data.results.map(
        (r) => ({
          id: r.id,
          confirmationNumber: r.confirmation_number,
          agent: r.agent ? `${r.agent.first_name} ${r.agent.last_name}` : void 0,
          userEmail: r.user_email,
          firstName: r.first_name,
          lastName: r.last_name,
          timestamp: r.timestamp,
          startLocation: r.start_location?.custom_name || r.start_location?.full_address || r.start_location?.formatted_address || "",
          endLocation: r.end_location?.custom_name || r.end_location?.full_address || r.end_location?.formatted_address || "",
          amountPaid: r.amount_paid.display,
          partnerRef: r.partner_ref,
          cancelled: r.cancelled,
          status: r.status,
          rebookedId: r.rebooked_id,
          trackingUrl: r.tracking_url,
          pickupDateTime: r.pickup_datetime
        })
      )
    };
  }
);
const voucherResponseSchema = z.union([
  z.object({
    voucher_base64_pdf: z.string()
  }),
  apiErrorSchema
]);
const getReservationVoucher = f$1.fetcher(
  async ({ ctx, get }, reservationId) => {
    const { reservationUrl } = urlConfig$1[ctx.env];
    const { data, response } = await get(
      `${reservationUrl}/${reservationId}/voucher/`,
      voucherResponseSchema
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if ("code" in data) {
      throw new Error(data.message ?? "Unknown error");
    }
    return {
      voucherBase64Pdf: data.voucher_base64_pdf
    };
  }
);
const resendResponseSchema = z.union([z.void(), apiErrorSchema]);
const resendConfirmationEmail = f$1.fetcher(
  async ({ ctx, post }, reservationId) => {
    const { reservationUrl } = urlConfig$1[ctx.env];
    const { data, response } = await post(
      `${reservationUrl}/${reservationId}/resend-confirmation-email/`,
      resendResponseSchema
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if (data && "code" in data) {
      throw new Error(data.message ?? "Unknown error");
    }
  }
);
const PAGE_SIZE_AGENTS = 25;
const travelAgentsResponseSchema = z.union([
  z.object({
    count: z.number(),
    results: z.array(
      z.object({
        id: z.number(),
        first_name: z.string(),
        last_name: z.string(),
        email: z.string()
      })
    )
  }),
  apiErrorSchema
]);
const getTravelAgents = f$1.fetcher(
  async ({ ctx, get }, {
    q,
    page = 1,
    pageSize = PAGE_SIZE_AGENTS,
    partnerRefs
  }) => {
    const { travelAgentsUrl } = urlConfig$1[ctx.env];
    const params = new URLSearchParams();
    params.set("page", page.toString());
    params.set("page_size", pageSize.toString());
    if (q) params.set("q", q);
    if (partnerRefs) {
      for (const ref of partnerRefs) {
        params.append("partner-ref", ref);
      }
    }
    const { data, response } = await get(
      `${travelAgentsUrl}/?${params.toString()}`,
      travelAgentsResponseSchema
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if ("code" in data) {
      throw new Error(data.message ?? "Unknown error");
    }
    return {
      count: data.count,
      pages: Math.ceil(data.count / PAGE_SIZE_AGENTS),
      results: data.results.map(
        (a) => ({
          id: a.id,
          firstName: a.first_name,
          lastName: a.last_name,
          email: a.email
        })
      )
    };
  }
);
const partnersResponseSchema = z.union([
  z.array(
    z.object({
      name: z.string(),
      ref: z.string()
    })
  ),
  apiErrorSchema
]);
const getPartners = f$1.fetcher(
  async ({ ctx, get }) => {
    const { partnersUrl } = urlConfig$1[ctx.env];
    const { data, response } = await get(
      `${partnersUrl}/`,
      partnersResponseSchema
    );
    if (response.status === 403) {
      throw new AuthError();
    }
    if (!Array.isArray(data) && "code" in data) {
      throw new Error(data.message ?? "Unknown error");
    }
    return data.map(
      (p) => ({
        label: p.name,
        value: p.ref
      })
    );
  }
);
const createPartnersQuery = f$1.combineFetchers({
  getReservations,
  getReservationVoucher,
  resendConfirmationEmail,
  getTravelAgents,
  getPartners
});

const urlConfig = {
  staging: {
    marketingUrl: "https://api-staging-internal.mozio.com/v2/auth/marketing"
  },
  production: {
    marketingUrl: "https://api.mozio.com/v2/auth/marketing"
  },
  testing: {
    marketingUrl: "https://api-testing.mozio.com/v2/auth/marketing"
  }
};
const f = new FetcherClient({
  ctx: z.object({
    env: envSchema
  })
});
z.enum([
  "API_INTEGRATION_OR_DISTRIBUTION",
  "TRANSPORTATION_PROVIDER",
  "SUPPORT_FOR_EXISTING_BOOKING",
  "OTHER"
]);
const contactResponseSchema = z.object({
  success: z.boolean()
});
const newsletterResponseSchema = z.object({
  success: z.boolean()
});
const submitContact = f.fetcher(
  async ({ ctx, post }, input) => {
    const { data } = await post(
      `${urlConfig[ctx.env].marketingUrl}/contact/`,
      contactResponseSchema,
      input
    );
    return data;
  }
);
const subscribeNewsletter = f.fetcher(
  async ({ ctx, post }, input) => {
    const { data } = await post(
      `${urlConfig[ctx.env].marketingUrl}/newsletter/subscribe`,
      newsletterResponseSchema,
      input
    );
    return data;
  }
);
const createMarketingQuery = f.combineFetchers({
  submitContact,
  subscribeNewsletter
});

export { AuthError, DISCOUNT_TYPE, DRIVER_TRIP_STATUS, ERROR_CODES, FLIGHT_DIRECTION, GetUserCountryResponseSchema, PARKING_FEATURE_TYPE, PARKING_STATUS_TYPE, PARKING_TRANSFER_TYPE, PURCHASE_ESIM_STATUS, USER_TYPE, createAuthDriverQuery, createAuthQuery, createChatQuery, createConfirmationQuery, createCurrencyQuery, createDriverQuery, createEngageQuery, createEsimQuery, createFlightQuery, createLocationQuery, createLoginQuery, createMarketingQuery, createParkingQuery, createPartnersDashboardAuthQuery, createPartnersQuery, createProviderDashboardAuthQuery, createProviderQuery, createReferralQuery, createTrackingQuery, createTransferKiwiQuery, createTransferQuery, createTravelAgentQuery, createUsersQuery, createWhiteLabelQuery, envSchema, whiteLabelServices };
